import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent, type WheelEvent } from 'react';
import { GState, jsPDF } from 'jspdf';
import { useAuth } from '../../context/AuthContext';
import { listarParametrosPublicos } from '../../lib';
import type {
  Conductor,
  FormaPagoPaquete,
  PagoPaquete,
  Paquete,
  Sucursal,
  TipoCliente,
  TipoEntregaViaje,
  TipoPagoParcial,
  Viaje,
} from '../../lib/appTypes';
import {
  EMPTY_DASHBOARD_STATS,
  getConductores,
  getVendedorDashboardStats,
  getSucursales,
  getViajeById,
  getViajes,
  getViajesPaginados,
  procesarGuiaPdf,
  saveViaje,
  subscribeStorage,
  uploadFileToMinio,
  procesarTransferenciaExcel,
  verificarDestinoGoogleMaps,
  type DashboardStatsData,
  type GuiaProcesada,
  type GuiaProcesadaExtraccion,
} from '../../lib/storage';
import MapaAdmin from '../admin/MapaAdmin';
import {
  ActivityFeed,
  DashboardStatCard,
  PanelShell,
  SectionSurface,
  WeeklyBarChart,
  type PanelNavItem,
} from '../../components/role-ui';
import { montoPorCobrarDe, pagoParaGuardar, validarMontoPorCobrar } from '../../lib/pagos';

type Section = 'dashboard' | 'entregas' | 'crear' | 'mapa';
type EstadoFiltro = 'todas' | Viaje['estado'];
type TipoEntregaFiltro = 'todas' | TipoEntregaViaje;
type PuntoIntermedioDraft = {
  id: string;
  urlDestino: string;
  destinoVerificado: { latitud: number; longitud: number } | null;
  error: string;
};

type ToastTone = 'error' | 'success';

type ToastState = {
  id: number;
  message: string;
  tone: ToastTone;
};

type EnvioPdfDraft = {
  logoUrl: string;
  vendedorNombre: string;
  entrega: string;
  cliente: string;
  identificacion: string;
  telefono: string;
  direccion: string;
  companiaTransporte: string;
};

const HISTORIAL_PAGE_SIZE = 10;

const SECTION_COPY: Record<Section, { label: string; icon: string; detail: string }> = {
  dashboard: {
    label: 'Dashboard',
    icon: 'fa-solid fa-chart-line',
    detail: 'Resumen de entregas creadas, conductores ocupados y actividad reciente de ventas.',
  },
  entregas: {
    label: 'Mis entregas',
    icon: 'fa-solid fa-boxes-stacked',
    detail: 'Consulta las entregas que has creado y su estado actual.',
  },
  crear: {
    label: 'Nueva entrega',
    icon: 'fa-solid fa-file-circle-plus',
    detail: 'Crea una nueva entrega con cliente y guía PDF. La asignación del conductor la realiza el administrador.',
  },
  mapa: {
    label: 'Mapa conductor',
    icon: 'fa-solid fa-location-dot',
    detail: 'El mapa solo consulta al conductor que selecciones dentro de esta vista.',
  },
};

const PAYMENT_LABELS: Record<Exclude<FormaPagoPaquete, ''>, string> = {
  efectivo: 'Efectivo',
  transferencia: 'Transferencia',
  cheque: 'Cheque',
  parcial: 'Por cobrar',
  nota_credito: 'Nota de crédito',
  completo: 'Pagado',
};

/** Formas de pago antiguas: ya no se ofrecen al crear, pero se muestran si una entrega las tiene. */
const FORMAS_PAGO_ANTERIORES: FormaPagoPaquete[] = ['efectivo', 'transferencia', 'cheque', 'nota_credito'];

const DELIVERY_TYPE_LABELS: Record<TipoEntregaViaje, string> = {
  entrega: 'Entrega',
  transferencia: 'Transferencia',
  envio: 'Envío',
};

const CLIENT_TYPE_OPTIONS: Array<{ value: Exclude<TipoCliente, ''>; label: string }> = [
  { value: 'cliente_final', label: 'Cliente final' },
  { value: 'aliado_estrategico', label: 'Aliado estrategico' },
];

function createEmptyPago(): PagoPaquete {
  return { tipo: '' };
}

function createEmptyPuntoIntermedio(): PuntoIntermedioDraft {
  return {
    id: crypto.randomUUID(),
    urlDestino: '',
    destinoVerificado: null,
    error: '',
  };
}

function createPagoForType(tipo: FormaPagoPaquete, total?: number | null): PagoPaquete {
  const montoInicial = typeof total === 'number' && !Number.isNaN(total) ? total : undefined;

  if (tipo === 'completo') {
    return {
      tipo,
      monto: montoInicial,
      medioPago: '',
    };
  }
  if (tipo === 'efectivo' || tipo === 'cheque') {
    return {
      tipo,
      monto: montoInicial,
    };
  }
  if (tipo === 'nota_credito') {
    return {
      tipo,
      valorNotaCredito: montoInicial,
    };
  }
  if (tipo === 'parcial') {
    return {
      tipo,
      tipoPagoParcial: '',
      montoPagado: 0,
    };
  }
  return { tipo };
}

function parseOptionalNumber(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function preventNumberInputWheelChange(event: WheelEvent<HTMLInputElement>): void {
  event.currentTarget.blur();
}

function parseParametroNumber(value?: string | null): number | undefined {
  if (!value?.trim()) return undefined;
  const parsed = Number(value.replace(',', '.'));
  if (!Number.isFinite(parsed) || parsed < 0) return undefined;
  return parsed;
}

function formatPaymentLabel(tipo?: FormaPagoPaquete): string {
  if (!tipo) return '—';
  return PAYMENT_LABELS[tipo as Exclude<FormaPagoPaquete, ''>] ?? tipo;
}

function formatDeliveryTypeLabel(tipo?: TipoEntregaViaje): string {
  if (!tipo) return '—';
  return DELIVERY_TYPE_LABELS[tipo] ?? tipo;
}

function validatePagoPaquete(pago?: PagoPaquete): string | null {
  if (!pago?.tipo) {
    return 'Selecciona una forma de pago para el paquete.';
  }

  if ((pago.tipo === 'efectivo' || pago.tipo === 'completo') && typeof pago.monto !== 'number') {
    return 'Debes ingresar el monto para la forma de pago seleccionada.';
  }
  if (pago.tipo === 'completo') {
    if (!pago.medioPago) {
      return 'Selecciona cómo fue pagado.';
    }
    if (pago.medioPago === 'transferencia' && (!pago.numeroTransferencia?.trim() || !pago.banco?.trim())) {
      return 'El pago por transferencia requiere número de transferencia y banco.';
    }
    if (pago.medioPago === 'cheque' && (!pago.numeroCheque?.trim() || !pago.banco?.trim())) {
      return 'El pago con cheque requiere número de cheque y banco.';
    }
    if (pago.medioPago === 'nota_credito' && !pago.numeroNotaCredito?.trim()) {
      return 'El pago con nota de crédito requiere el número de la nota.';
    }
  }
  if (pago.tipo === 'cheque' && (!pago.numeroCheque?.trim() || !pago.banco?.trim() || typeof pago.monto !== 'number')) {
    return 'El cheque requiere número, banco y monto.';
  }
  if (pago.tipo === 'nota_credito' && (!pago.numeroNotaCredito?.trim() || typeof pago.valorNotaCredito !== 'number')) {
    return 'La nota de crédito requiere número y valor.';
  }
  if (pago.tipo === 'parcial' && !pago.tipoPagoParcial) {
    return 'Selecciona la forma de cobro sugerida.';
  }

  return null;
}

function paymentRequiredForDeliveryType(tipoEntrega: TipoEntregaViaje): boolean {
  return tipoEntrega !== 'transferencia';
}

function createEmptyPaquete(): Paquete {
  return {
    nombre: '',
    descripcion: '',
    imagenes: [],
    cliente: {
      nombre: '',
      numero: '',
      fechaDocumento: '',
      ciudad: '',
      sucursal: '',
      correoElectronico: '',
      tipoCliente: '',
    },
    guiaPdfUrl: '',
    guiaPdfNombreArchivo: '',
    envioPdfUrl: '',
    envioPdfNombreArchivo: '',
    pago: createEmptyPago(),
  };
}

function estadoLabel(viaje: Viaje): string {
  if (viaje.estado === 'en_curso') return 'En camino';
  if (viaje.estado === 'finalizado') return 'Entregada';
  return 'Asignada';
}

function estadoBadge(viaje: Viaje): string {
  if (viaje.estado === 'en_curso') return 'border border-blue-200 bg-blue-50 text-blue-700';
  if (viaje.estado === 'finalizado') return 'border border-emerald-200 bg-emerald-50 text-emerald-700';
  return 'border border-amber-200 bg-amber-50 text-amber-800';
}

function historialEstadoLabel(viaje: Viaje): string {
  if (viaje.esDevolucion) return 'Devolución';
  return estadoLabel(viaje);
}

function historialEstadoBadge(viaje: Viaje): string {
  if (viaje.esDevolucion) return 'border border-rose-200 bg-rose-50 text-rose-700';
  return estadoBadge(viaje);
}

function formatDateTime(timestamp?: number): string {
  if (!timestamp) return 'Sin fecha registrada';
  return new Intl.DateTimeFormat('es-EC', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(timestamp));
}

function mergeViajes(current: Viaje[], incoming: Viaje[]): Viaje[] {
  const byId = new Map(current.map(viaje => [viaje.id, viaje]));
  for (const viaje of incoming) {
    byId.set(viaje.id, viaje);
  }

  return [...byId.values()].sort((left, right) => {
    const leftDate = left.fechaFin ?? left.fechaInicio ?? 0;
    const rightDate = right.fechaFin ?? right.fechaInicio ?? 0;
    return rightDate - leftDate;
  });
}

function sameDay(timestampA: number, timestampB: number): boolean {
  const left = new Date(timestampA);
  const right = new Date(timestampB);
  return left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate();
}

function formatShortDateTime(timestamp?: number): string {
  if (!timestamp) return 'Ahora';
  return new Intl.DateTimeFormat('es-EC', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(timestamp));
}

function formatMoney(value?: number | null): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('es-EC', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Máximo de caracteres de "Detalle/Logístico a Asignar" (Paquete.nombre en el backend). */
const MAX_DETALLE_ENTREGA = 700;
/** A partir de cuántos caracteres restantes el contador se pone en rojo. */
const AVISO_DETALLE_RESTANTES = 10;

/** Nombre visible de los campos del formulario según su ruta en el error del backend. */
const ETIQUETAS_CAMPOS_ENTREGA: Record<string, string> = {
  'paquete.nombre': 'Detalle/Logístico a Asignar',
  'paquete.descripcion': 'Descripción',
  'paquete.cliente.nombre': 'Cliente',
  'paquete.cliente.numero': 'Número de factura',
  'paquete.cliente.telefono': 'Teléfono / celular',
  'paquete.cliente.direccion': 'Dirección',
  'paquete.cliente.correo_electronico': 'Correo electrónico',
  url_destino: 'URL de destino',
  tiempo_estimado_minutos: 'Tiempo estimado',
};

/** Primer error de validación del backend ({data: {campo: [mensaje]}}) como "Campo: mensaje" y su ruta. */
function leerErrorValidacion(err: unknown): { ruta: string; mensaje: string } | null {
  const buscar = (valor: unknown, ruta: string[]): { ruta: string; mensaje: string } | null => {
    if (typeof valor === 'string') return { ruta: ruta.join('.'), mensaje: valor };
    if (Array.isArray(valor)) {
      for (const item of valor) {
        const encontrado = buscar(item, ruta);
        if (encontrado) return encontrado;
      }
      return null;
    }
    if (valor && typeof valor === 'object') {
      for (const [clave, item] of Object.entries(valor)) {
        const encontrado = buscar(item, [...ruta, clave]);
        if (encontrado) return encontrado;
      }
    }
    return null;
  };
  const data = (err as { data?: unknown } | null)?.data;
  if (!data || typeof data !== 'object') return null;
  const encontrado = buscar(data, []);
  if (!encontrado) return null;
  const etiqueta = ETIQUETAS_CAMPOS_ENTREGA[encontrado.ruta] ?? encontrado.ruta.split('.').pop() ?? '';
  return { ruta: encontrado.ruta, mensaje: etiqueta ? `${etiqueta}: ${encontrado.mensaje}` : encontrado.mensaje };
}

function getTripDocumentos(viaje: Viaje): { title: string; documentos: Array<{ url: string; nombre: string }> } {
  if (viaje.tipoEntrega === 'envio' && viaje.paquete?.envioPdfUrl) {
    return {
      title: 'PDF de envío',
      documentos: [{
        url: viaje.paquete.envioPdfUrl,
        nombre: viaje.paquete.envioPdfNombreArchivo || 'PDF de envío',
      }],
    };
  }

  const firmadas = (viaje.guiasFirmadas ?? []).filter(guia => guia.url);
  if (firmadas.length > 0) {
    return {
      title: 'Guías firmadas',
      documentos: firmadas.map((guia, index) => ({
        url: guia.url,
        nombre: guia.nombreArchivo || `Guía firmada ${index + 1}`,
      })),
    };
  }

  const generadas = (viaje.paquete?.guiasPdf ?? []).filter(guia => guia.url);
  if (generadas.length > 0) {
    return {
      title: 'Guías generadas',
      documentos: generadas.map((guia, index) => ({
        url: guia.url,
        nombre: guia.nombreArchivo || `Guía ${index + 1}`,
      })),
    };
  }

  if (viaje.paquete?.guiaPdfUrl) {
    return {
      title: 'Guía generada',
      documentos: [{
        url: viaje.paquete.guiaPdfUrl,
        nombre: viaje.paquete.guiaPdfNombreArchivo || 'Guía generada',
      }],
    };
  }

  return { title: 'Guías', documentos: [] };
}

function getTripInvoiceNumbers(viaje: Viaje): string[] {
  const invoiceNumbers = (viaje.paquete?.guiasPdf ?? [])
    .map(guia => guia.numeroFactura?.trim())
    .filter((numeroFactura): numeroFactura is string => Boolean(numeroFactura));

  if (invoiceNumbers.length > 0) {
    return Array.from(new Set(invoiceNumbers));
  }

  const clienteNumero = viaje.paquete?.cliente?.numero?.trim();
  return clienteNumero ? [clienteNumero] : [];
}

function isSpreadsheetGuide(fileName?: string): boolean {
  const normalized = (fileName || '').trim().toLowerCase();
  return normalized.endsWith('.xlsx') || normalized.endsWith('.xls');
}

function buildPaqueteDescripcion(items: GuiaProcesadaExtraccion['items']): string {
  return Array.from(new Set(
    items
      .map(item => item.descripcion.trim())
      .filter(Boolean),
  )).join(', ');
}

function buildTransferenciaDescripcion(extraccion: GuiaProcesadaExtraccion): string {
  const transferencia = extraccion.transferencia;
  if (!transferencia) return '';

  if (transferencia.descripcion.trim()) {
    return transferencia.descripcion.trim();
  }

  return Array.from(new Set(
    transferencia.items
      .map(item => item.producto.trim())
      .filter(Boolean),
  )).join(', ');
}

function formatFacturaLabel(numeroFactura?: string): string {
  const normalizedNumber = numeroFactura?.trim();
  return normalizedNumber || 'Sin numero de factura';
}

function buildGuiaDescripcion(guia: GuiaProcesada): string {
  if (guia.extraccion.tipoDocumento === 'transferencia') {
    const descripcionTransferencia = buildTransferenciaDescripcion(guia.extraccion);
    const numeroTransferencia = guia.extraccion.transferencia?.numero?.trim() || formatFacturaLabel(guia.cliente.numero);
    return descripcionTransferencia
      ? `Transferencia: ${numeroTransferencia}\n${descripcionTransferencia}`
      : `Transferencia: ${numeroTransferencia}`;
  }

  const descripcion = buildPaqueteDescripcion(guia.extraccion.items);
  const factura = formatFacturaLabel(guia.cliente.numero);

  return descripcion
    ? `Factura: ${factura}\n${descripcion}`
    : `Factura: ${factura}`;
}

function buildGuiasDescripcion(guias: GuiaProcesada[]): string {
  return guias.map(buildGuiaDescripcion).join('\n\n');
}

function sumGuiasTotal(guias: GuiaProcesada[]): number | undefined {
  let hasTotal = false;
  const total = guias.reduce((accumulator, guia) => {
    if (typeof guia.extraccion.total !== 'number' || Number.isNaN(guia.extraccion.total)) {
      return accumulator;
    }

    hasTotal = true;
    return accumulator + guia.extraccion.total;
  }, 0);

  return hasTotal ? total : undefined;
}

function getTransferUnits(guia: GuiaProcesada): number | undefined {
  const transferencia = guia.extraccion.transferencia;
  if (typeof transferencia?.totalUnidades === 'number' && !Number.isNaN(transferencia.totalUnidades)) {
    return transferencia.totalUnidades;
  }
  if (typeof transferencia?.totalProductos === 'number' && !Number.isNaN(transferencia.totalProductos)) {
    return transferencia.totalProductos;
  }

  const sumatoriaItems = transferencia?.items.reduce((sum, item) => sum + (item.cantidad ?? 0), 0) ?? 0;
  return sumatoriaItems > 0 ? sumatoriaItems : undefined;
}

function getTransferItemCount(guia: GuiaProcesada): number | undefined {
  const transferencia = guia.extraccion.transferencia;
  if (typeof transferencia?.totalItems === 'number' && !Number.isNaN(transferencia.totalItems)) {
    return transferencia.totalItems;
  }

  const itemsCount = transferencia?.items.length ?? 0;
  return itemsCount > 0 ? itemsCount : undefined;
}

function sumTransferUnits(guias: GuiaProcesada[]): number | undefined {
  let hasTotal = false;
  const total = guias.reduce((accumulator, guia) => {
    const unidades = getTransferUnits(guia);
    if (typeof unidades === 'number' && !Number.isNaN(unidades)) {
      hasTotal = true;
      return accumulator + unidades;
    }
    return accumulator;
  }, 0);

  return hasTotal ? total : undefined;
}

function sumTransferItems(guias: GuiaProcesada[]): number | undefined {
  let hasTotal = false;
  const total = guias.reduce((accumulator, guia) => {
    const totalItems = getTransferItemCount(guia);
    if (typeof totalItems === 'number' && !Number.isNaN(totalItems)) {
      hasTotal = true;
      return accumulator + totalItems;
    }
    return accumulator;
  }, 0);

  return hasTotal ? total : undefined;
}

function sanitizePdfFileName(value: string): string {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalized || 'envio';
}

function getImageFormat(dataUrl: string): 'PNG' | 'JPEG' | 'WEBP' {
  if (dataUrl.startsWith('data:image/jpeg') || dataUrl.startsWith('data:image/jpg')) return 'JPEG';
  if (dataUrl.startsWith('data:image/webp')) return 'WEBP';
  return 'PNG';
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function loadImageAsDataUrl(url: string): Promise<string | null> {
  const normalizedUrl = url.trim();
  if (!normalizedUrl) return null;

  try {
    const response = await fetch(normalizedUrl);
    if (!response.ok) return null;
    return await blobToDataUrl(await response.blob());
  } catch {
    return null;
  }
}

function downloadGeneratedFile(file: File): void {
  const objectUrl = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

async function buildEnvioPdfFile(draft: EnvioPdfDraft): Promise<File> {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const tableWidth = pageWidth - (margin * 2);
  let currentY = 14;

  const logoDataUrl = await loadImageAsDataUrl(draft.logoUrl);
  if (logoDataUrl) {
    const watermarkWidth = Math.min(pageWidth * 0.52, 110);
    const watermarkHeight = watermarkWidth * 0.42;
    const watermarkX = (pageWidth - watermarkWidth) / 2;
    const watermarkY = (pageHeight - watermarkHeight) / 2;

    doc.saveGraphicsState();
    doc.setGState(new GState({ opacity: 0.12 }));
    doc.addImage(logoDataUrl, getImageFormat(logoDataUrl), watermarkX, watermarkY, watermarkWidth, watermarkHeight);
    doc.restoreGraphicsState();
  }

  if (logoDataUrl) {
    const logoWidth = 48;
    const logoHeight = 20;
    const logoX = (pageWidth - logoWidth) / 2;
    doc.addImage(logoDataUrl, getImageFormat(logoDataUrl), logoX, currentY, logoWidth, logoHeight);
    currentY += logoHeight + 3;
  }

  if (draft.vendedorNombre.trim()) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Vendedor/a:', margin, currentY + 4);
    doc.setFont('helvetica', 'normal');
    doc.text(draft.vendedorNombre.trim(), margin + 22, currentY + 4);
    currentY += 10;
  }

  doc.setFontSize(14);
  const entregaLines = doc.splitTextToSize(draft.entrega.trim() || '-', tableWidth - 12);
  const topRowHeight = Math.max(18, 10 + (entregaLines.length * 6));
  const labelColumnWidth = 40;
  const valueColumnX = margin + labelColumnWidth + 4;
  const valueColumnWidth = tableWidth - labelColumnWidth - 8;

  const detailRows: Array<[string, string]> = [
    ['CLIENTE:', draft.cliente.trim() || '-'],
    ['CI:', draft.identificacion.trim() || '-'],
    ['#TELF:', draft.telefono.trim() || '-'],
    ['DIRECCION:', draft.direccion.trim() || '-'],
    ['TRANSPORTE:', (draft.companiaTransporte?.trim() || '-').toUpperCase()],
  ];

  const rowHeights = detailRows.map(([, value]) => {
    const wrappedValue = doc.splitTextToSize(value, valueColumnWidth);
    return Math.max(12, 6 + (wrappedValue.length * 5.2));
  });

  const detailHeight = rowHeights.reduce((sum, rowHeight) => sum + rowHeight, 0);
  const detailSectionY = currentY + topRowHeight;

  doc.setDrawColor(120, 113, 108);
  doc.rect(margin, currentY, tableWidth, topRowHeight + detailHeight);
  doc.line(margin, detailSectionY, margin + tableWidth, detailSectionY);
  doc.line(margin + labelColumnWidth, detailSectionY, margin + labelColumnWidth, detailSectionY + detailHeight);

  doc.setFont('helvetica', 'normal');
  doc.text(entregaLines, margin + (tableWidth / 2), currentY + (topRowHeight / 2) + 2, { align: 'center' });

  doc.setFontSize(11);
  let rowTop = detailSectionY;
  detailRows.forEach(([label, value], index) => {
    const rowHeight = rowHeights[index] ?? 12;
    const textY = rowTop + 8;
    const wrappedValue = doc.splitTextToSize(value, valueColumnWidth);

    doc.setFont('helvetica', 'bold');
    doc.text(label, margin + 4, textY);
    doc.setFont('helvetica', label.startsWith('TRANSPORTE') ? 'bold' : 'normal');
    doc.text(wrappedValue, valueColumnX, textY);

    rowTop += rowHeight;
    if (index < detailRows.length - 1) {
      doc.line(margin, rowTop, margin + tableWidth, rowTop);
    }
  });

  const fileName = `${sanitizePdfFileName(draft.entrega)}-envio.pdf`;
  return new File([doc.output('blob') as Blob], fileName, { type: 'application/pdf' });
}

function hasMapData(viaje: Viaje): boolean {
  return Boolean(
    viaje.coordenadas.length > 0
    || viaje.coordenadaActual
    || viaje.coordenadaInicio
    || viaje.coordenadaFin
    || viaje.puntosIntermedios.length > 0,
  );
}

function findMatchingSucursalName(value: string | undefined, sucursales: Sucursal[]): string {
  const normalizedValue = value?.trim().toLowerCase();
  if (!normalizedValue) return '';

  const match = sucursales.find(sucursal => sucursal.nombre.trim().toLowerCase() === normalizedValue);
  return match?.nombre ?? '';
}

function buildTransferRouteName(origen: string, destino: string): string {
  const parts = [origen.trim(), destino.trim()].filter(Boolean);
  return parts.join('/');
}

function buildProcessedCliente(guia: GuiaProcesada | undefined, sucursales: Sucursal[], fallbackSucursal = '') {
  if (!guia) {
    return {
      nombre: '',
      numero: '',
      fechaDocumento: '',
      ciudad: '',
      sucursal: fallbackSucursal,
      correoElectronico: '',
      direccion: '',
      telefono: '',
      tipoCliente: '',
    };
  }

  return {
    ...guia.cliente,
    sucursal: findMatchingSucursalName(guia.cliente.sucursal, sucursales) || fallbackSucursal || '',
    correoElectronico: guia.cliente.correoElectronico || '',
  };
}

function normalizeClientName(value?: string | null): string {
  return (value || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function getHistoricalDestinationUrlsByClientName(viajes: Viaje[], clientName: string): string[] {
  const normalizedClientName = normalizeClientName(clientName);
  if (!normalizedClientName) return [];

  const seen = new Set<string>();

  return [...viajes]
    .filter(viaje => {
      if (viaje.tipoEntrega === 'transferencia') return false;
      if (!viaje.urlDestino?.trim()) return false;

      return normalizeClientName(viaje.paquete?.cliente?.nombre) === normalizedClientName;
    })
    .sort((left, right) => {
      const leftTimestamp = left.createdAt ?? left.fechaFin ?? left.fechaInicio ?? 0;
      const rightTimestamp = right.createdAt ?? right.fechaFin ?? right.fechaInicio ?? 0;
      return rightTimestamp - leftTimestamp;
    })
    .map(viaje => viaje.urlDestino?.trim() || '')
    .filter(url => {
      if (!url || seen.has(url)) return false;
      seen.add(url);
      return true;
    });
}

function supportsDestinationHistory(tipoEntrega: TipoEntregaViaje): boolean {
  return tipoEntrega === 'entrega';
}

function looksLikeGoogleMapsUrl(value: string): boolean {
  const normalizedValue = value.trim().toLowerCase();
  if (!normalizedValue.startsWith('http://') && !normalizedValue.startsWith('https://')) {
    return false;
  }

  return normalizedValue.includes('google.') || normalizedValue.includes('goo.gl') || normalizedValue.includes('maps.app.goo.gl');
}

function getEnvioPdfDraftError(draft: EnvioPdfDraft | null): string | null {
  if (!draft) return 'Abre el formulario del PDF de envío para completar los datos requeridos.';
  if (!draft.entrega.trim()) return 'Completa el campo Destino/Transporte para generar el PDF de envío.';
  if (!draft.direccion.trim()) return 'Completa el campo Dirección para generar el PDF de envío.';
  if (!draft.cliente.trim()) return 'Completa el campo Cliente para generar el PDF de envío.';
  if (!draft.identificacion.trim()) return 'Completa el campo CI para generar el PDF de envío.';
  if (!draft.telefono.trim()) return 'Completa el campo #TELF para generar el PDF de envío.';
  if (!draft.companiaTransporte.trim()) return 'Completa el campo Compañía transporte para generar el PDF de envío.';
  return null;
}

export default function VendedorPanel() {
  const { user, logout } = useAuth();
  const [section, setSection] = useState<Section>('dashboard');
  const [conductores, setConductores] = useState<Conductor[]>([]);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [entregas, setEntregas] = useState<Viaje[]>([]);
  const [entregasTotalRegistros, setEntregasTotalRegistros] = useState(0);
  const [entregasActivasTotal, setEntregasActivasTotal] = useState(0);
  const [entregasCargando, setEntregasCargando] = useState(false);
  const [saving, setSaving] = useState(false);
  /** Ruta del campo que el backend rechazó (p. ej. 'paquete.nombre'), para pintarlo en rojo. */
  const [campoConError, setCampoConError] = useState('');
  const detalleEntregaRef = useRef<HTMLTextAreaElement>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [mapaConductorId, setMapaConductorId] = useState('');
  const [mapaViaje, setMapaViaje] = useState<Viaje | null>(null);
  const [mapaViajeId, setMapaViajeId] = useState<string | null>(null);
  const [mapaCargando, setMapaCargando] = useState(false);
  const [mapaError, setMapaError] = useState('');
  const [entregasConductorSeleccionado, setEntregasConductorSeleccionado] = useState<Viaje[]>([]);
  const [historialMapaViaje, setHistorialMapaViaje] = useState<Viaje | null>(null);
  const [detalleEntrega, setDetalleEntrega] = useState<Viaje | null>(null);
  const [entregasPagina, setEntregasPagina] = useState(1);
  const [paquete, setPaquete] = useState<Paquete>(createEmptyPaquete());
  const [tipoEntrega, setTipoEntrega] = useState<TipoEntregaViaje>('entrega');
  const [transferenciaDesdeSucursal, setTransferenciaDesdeSucursal] = useState('');
  const [transferenciaHaciaSucursal, setTransferenciaHaciaSucursal] = useState('');
  const [companiaTransporte, setCompaniaTransporte] = useState('');
  const [valorTotalEnvio, setValorTotalEnvio] = useState<number | undefined>(undefined);
  const [urlDestino, setUrlDestino] = useState('');
  const [isDestinationHistoryOpen, setIsDestinationHistoryOpen] = useState(false);
  const [tiempoEstimadoMinutos, setTiempoEstimadoMinutos] = useState(60);
  const [guiaProcesando, setGuiaProcesando] = useState(false);
  const [guiaError, setGuiaError] = useState('');
  const [transferenciaExcelProcesando, setTransferenciaExcelProcesando] = useState(false);
  const [transferenciaExcelError, setTransferenciaExcelError] = useState('');
  const [guiasProcesadas, setGuiasProcesadas] = useState<GuiaProcesada[]>([]);
  const [verificandoDestino, setVerificandoDestino] = useState(false);
  const [destinoVerificado, setDestinoVerificado] = useState<{ latitud: number; longitud: number } | null>(null);
  const [destinoError, setDestinoError] = useState('');
  const [puntosIntermedios, setPuntosIntermedios] = useState<PuntoIntermedioDraft[]>([]);
  const [verificandoPuntoId, setVerificandoPuntoId] = useState<string | null>(null);
  const [estadoFiltro, setEstadoFiltro] = useState<EstadoFiltro>('todas');
  const [tipoEntregaFiltro, setTipoEntregaFiltro] = useState<TipoEntregaFiltro>('todas');
  const [devolucionFiltro, setDevolucionFiltro] = useState<'todas' | 'si' | 'no'>('todas');
  const [busqueda, setBusqueda] = useState('');
  const [dashboardStats, setDashboardStats] = useState<DashboardStatsData>(EMPTY_DASHBOARD_STATS);
  const [logoFacturaUrl, setLogoFacturaUrl] = useState('');
  const [envioPdfDraft, setEnvioPdfDraft] = useState<EnvioPdfDraft | null>(null);
  const [preparandoEnvioPdf, setPreparandoEnvioPdf] = useState(false);
  const [generandoEnvioPdf, setGenerandoEnvioPdf] = useState(false);
  const [envioPdfError, setEnvioPdfError] = useState('');
  const [historicalDestinationUrls, setHistoricalDestinationUrls] = useState<string[]>([]);
  const guiaPrincipal = guiasProcesadas[0];
  const clienteNombreActual = paquete.cliente?.nombre ?? '';
  const totalGuiasProcesadas = useMemo(() => sumGuiasTotal(guiasProcesadas), [guiasProcesadas]);
  const totalItemsTransferencia = useMemo(() => sumTransferItems(guiasProcesadas), [guiasProcesadas]);
  const totalProductosTransferencia = useMemo(() => sumTransferUnits(guiasProcesadas), [guiasProcesadas]);
  const envioPdfDraftValidationError = getEnvioPdfDraftError(envioPdfDraft);
  const previewDestinoCoordinate = useMemo(() => {
    if (!destinoVerificado) return undefined;

    return {
      lat: destinoVerificado.latitud,
      lng: destinoVerificado.longitud,
      timestamp: Date.now(),
    };
  }, [destinoVerificado]);
  const mostrandoTransferencia = tipoEntrega === 'transferencia';
  const destinationHistoryEnabled = supportsDestinationHistory(tipoEntrega);
  const envioPdfGenerado = Boolean(paquete.envioPdfUrl?.trim() && paquete.envioPdfNombreArchivo?.trim());
  const envioPdfPendiente = tipoEntrega === 'envio' && !envioPdfGenerado;

  function showToast(message: string, tone: ToastTone = 'error') {
    setToast({
      id: Date.now(),
      message,
      tone,
    });
  }

  function dismissToast() {
    setToast(null);
  }

  useEffect(() => {
    if (!toast) return undefined;

    const timeoutId = window.setTimeout(() => {
      setToast(current => (current?.id === toast.id ? null : current));
    }, 4200);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [toast]);

  async function refreshGeneral() {
    const [conductoresResult, sucursalesResult, dashboardResult, entregasResult, entregasActivasResult] = await Promise.allSettled([
      getConductores(),
      getSucursales(),
      getVendedorDashboardStats(),
      getViajesPaginados({ page: 1, page_size: 1 }),
      getViajesPaginados({ estado: 'en_curso', page: 1, page_size: 1 }),
    ]);

    if (conductoresResult.status === 'fulfilled') {
      setConductores(conductoresResult.value.filter(conductor => conductor.activo));
    }
    if (sucursalesResult.status === 'fulfilled') {
      setSucursales(sucursalesResult.value);
    }
    if (dashboardResult.status === 'fulfilled') {
      setDashboardStats(dashboardResult.value);
    }
    if (entregasResult.status === 'fulfilled') {
      setEntregasTotalRegistros(entregasResult.value.count);
    }
    if (entregasActivasResult.status === 'fulfilled') {
      setEntregasActivasTotal(entregasActivasResult.value.count);
    }

    const firstFailure = [conductoresResult, sucursalesResult, dashboardResult, entregasResult, entregasActivasResult]
      .find(result => result.status === 'rejected') as PromiseRejectedResult | undefined;

    if (firstFailure) {
      showToast((firstFailure.reason as { message?: string })?.message ?? 'No se pudieron cargar algunos datos del panel.');
      return;
    }
  }

  async function refreshEntregas(
    page = entregasPagina,
    search = busqueda,
    estado = estadoFiltro,
    tipoEntrega = tipoEntregaFiltro,
    devolucion = devolucionFiltro,
  ) {
    try {
      setEntregasCargando(true);
      const result = await getViajesPaginados({
        page,
        page_size: HISTORIAL_PAGE_SIZE,
        search: search.trim() || undefined,
        estado: estado === 'todas' ? undefined : estado === 'finalizado' ? 'completado' : estado,
        tipo_entrega: tipoEntrega !== 'todas' ? tipoEntrega : undefined,
        es_devolucion: devolucion === 'todas' ? undefined : devolucion === 'si',
      });

      setEntregas(result.items);
      setEntregasTotalRegistros(result.count);
    } catch (err: unknown) {
      showToast((err as { message?: string })?.message ?? 'No se pudieron cargar las entregas filtradas.');
    } finally {
      setEntregasCargando(false);
    }
  }

  useEffect(() => {
    void refreshGeneral();
    // Los datos de conductores, sucursales y dashboard no cambian
    // frecuentemente — una sola carga al montar es suficiente.
    return () => {};
  }, []);

  useEffect(() => {
    if (section !== 'crear') return;

    void refreshGeneral();
    // Sin intervalo — los datos se cargan una vez al entrar a "crear".
    // Solo el mapa de conductor necesita polling en tiempo real.
  }, [section]);

  useEffect(() => {
    if (section !== 'entregas') return undefined;

    void refreshEntregas();
    const unsubscribe = subscribeStorage(() => {
      void refreshEntregas();
    });

    return () => {
      unsubscribe();
    };
  }, [section, entregasPagina, busqueda, devolucionFiltro, estadoFiltro, tipoEntregaFiltro]);

  useEffect(() => {
    if (section !== 'entregas') return;
    setEntregasPagina(1);
  }, [section]);

  useEffect(() => {
    setEntregasPagina(1);
  }, [busqueda, devolucionFiltro, estadoFiltro, tipoEntregaFiltro]);

  const conductoresConEntregas = useMemo(() => {
    return conductores;
  }, [conductores]);

  useEffect(() => {
    if (!mapaConductorId) return;
    if (conductoresConEntregas.some(conductor => conductor.id === mapaConductorId)) return;

    setMapaConductorId('');
    setMapaViaje(null);
    setMapaViajeId(null);
    setEntregasConductorSeleccionado([]);
    setMapaError('');
  }, [conductoresConEntregas, mapaConductorId]);

  useEffect(() => {
    if (destinationHistoryEnabled && historicalDestinationUrls.length > 0) return;
    setIsDestinationHistoryOpen(false);
  }, [destinationHistoryEnabled, historicalDestinationUrls.length]);

  useEffect(() => {
    if (tipoEntrega !== 'entrega') return;
    if (!historicalDestinationUrls.length) return;

    setUrlDestino(current => (current.trim() ? current : historicalDestinationUrls[0] || ''));
  }, [historicalDestinationUrls, tipoEntrega]);

  useEffect(() => {
    const normalizedClientName = clienteNombreActual.trim();
    if (!supportsDestinationHistory(tipoEntrega) || !normalizedClientName) {
      setHistoricalDestinationUrls([]);
      return undefined;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        const result = await getViajesPaginados({
          page: 1,
          page_size: 50,
          search: normalizedClientName,
        });

        if (cancelled) return;
        setHistoricalDestinationUrls(getHistoricalDestinationUrlsByClientName(result.items, normalizedClientName));
      } catch {
        if (!cancelled) {
          setHistoricalDestinationUrls([]);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [clienteNombreActual, tipoEntrega]);

  useEffect(() => {
    const normalizedUrl = urlDestino.trim();

    if (tipoEntrega !== 'entrega' || !normalizedUrl) {
      setVerificandoDestino(false);
      setDestinoVerificado(null);
      setDestinoError('');
      return;
    }

    if (!looksLikeGoogleMapsUrl(normalizedUrl)) {
      setVerificandoDestino(false);
      setDestinoVerificado(null);
      setDestinoError('');
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setVerificandoDestino(true);
      try {
        const coordinates = await verificarDestinoGoogleMaps(normalizedUrl);
        if (cancelled) return;

        setDestinoVerificado(coordinates);
        setDestinoError('');
      } catch (err: unknown) {
        if (cancelled) return;

        setDestinoVerificado(null);
        setDestinoError((err as { message?: string })?.message ?? 'No se pudieron obtener las coordenadas del destino.');
      } finally {
        if (!cancelled) {
          setVerificandoDestino(false);
        }
      }
    }, 450);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [tipoEntrega, urlDestino]);

  const puntosIntermediosRegistrados = useMemo(() => {
    return puntosIntermedios
      .filter(point => point.urlDestino.trim())
      .map(point => ({
        urlDestino: point.urlDestino.trim(),
        latitud: point.destinoVerificado?.latitud,
        longitud: point.destinoVerificado?.longitud,
      }));
  }, [puntosIntermedios]);

  useEffect(() => {
    const total = totalGuiasProcesadas;
    if (typeof total !== 'number' || Number.isNaN(total)) return;

    setPaquete(prev => {
      const pago = prev.pago;
      if (!pago?.tipo) return prev;

      if (pago.tipo === 'completo') {
        if (pago.monto === total) return prev;
        return {
          ...prev,
          pago: {
            ...pago,
            monto: total,
          },
        };
      }

      if (pago.tipo === 'efectivo' || pago.tipo === 'cheque') {
        if (typeof pago.monto === 'number') return prev;
        return {
          ...prev,
          pago: {
            ...pago,
            monto: total,
          },
        };
      }

      if (pago.tipo === 'nota_credito') {
        if (typeof pago.valorNotaCredito === 'number') return prev;
        return {
          ...prev,
          pago: {
            ...pago,
            valorNotaCredito: total,
          },
        };
      }

      return prev;
    });
  }, [totalGuiasProcesadas]);

  useEffect(() => {
    const selectedSucursal = paquete.cliente?.sucursal?.trim();
    if (!selectedSucursal) return;
    if (sucursales.some(sucursal => sucursal.nombre === selectedSucursal)) return;

    setPaquete(prev => ({
      ...prev,
      cliente: prev.cliente
        ? {
            ...prev.cliente,
            sucursal: '',
          }
        : prev.cliente,
    }));
  }, [paquete.cliente?.sucursal, sucursales]);

  useEffect(() => {
    if (section !== 'mapa' || !mapaConductorId) return;

    let cancelled = false;
    const run = async () => {
      try {
        if (!cancelled) {
          setMapaCargando(true);
        }

        const [nextEntregasConductor, nextViajes] = await Promise.all([
          getViajes({ conductor: Number(mapaConductorId) }),
          getViajes({ conductor: Number(mapaConductorId), estado: 'en_curso' }),
        ]);

        if (cancelled) return;

        setEntregasConductorSeleccionado(nextEntregasConductor);

        const [activeViaje] = [...nextViajes].sort((left, right) => {
          const leftDate = left.fechaInicio ?? left.fechaFin ?? 0;
          const rightDate = right.fechaInicio ?? right.fechaFin ?? 0;
          return rightDate - leftDate;
        });

        if (!activeViaje) {
          setMapaViaje(null);
          setMapaViajeId(null);
          setMapaError('');
          return;
        }

        const detailedViaje = await getViajeById(activeViaje.id) ?? activeViaje;

        if (cancelled) return;

        setEntregasConductorSeleccionado(mergeViajes(nextEntregasConductor, [detailedViaje]));
        setMapaViaje(detailedViaje);
        setMapaViajeId(detailedViaje.id);
        setMapaError('');
      } catch (err: unknown) {
        if (cancelled) return;
        setMapaError((err as { message?: string })?.message ?? 'No se pudo actualizar el mapa del conductor.');
      } finally {
        if (!cancelled) {
          setMapaCargando(false);
        }
      }
    };

    void run();
    const unsubscribe = subscribeStorage(() => {
      void run();
    });
    const interval = setInterval(() => {
      void run();
    }, 10000);

    return () => {
      cancelled = true;
      unsubscribe();
      clearInterval(interval);
    };
  }, [mapaConductorId, section]);

  const conductorMapa = mapaConductorId
    ? conductores.find(conductor => conductor.id === mapaConductorId)
    : undefined;

  const entregasTotalPaginas = useMemo(
    () => Math.max(1, Math.ceil(entregasTotalRegistros / HISTORIAL_PAGE_SIZE)),
    [entregasTotalRegistros],
  );
  const entregasPaginaActual = Math.min(entregasPagina, entregasTotalPaginas);

  useEffect(() => {
    setEntregasPagina(current => Math.min(current, entregasTotalPaginas));
  }, [entregasTotalPaginas]);

  const navItems: PanelNavItem[] = [
    { key: 'dashboard', label: SECTION_COPY.dashboard.label, icon: SECTION_COPY.dashboard.icon },
    { key: 'entregas', label: SECTION_COPY.entregas.label, icon: SECTION_COPY.entregas.icon, count: entregasTotalRegistros || undefined },
    { key: 'crear', label: SECTION_COPY.crear.label, icon: SECTION_COPY.crear.icon },
    {
      key: 'mapa',
      label: SECTION_COPY.mapa.label,
      icon: SECTION_COPY.mapa.icon,
      count: entregasActivasTotal || undefined,
    },
  ];

  const inputCls = 'w-full rounded-[14px] border border-[var(--freisa-border)] bg-white px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10';
  const primaryButtonCls = 'rounded-[14px] bg-[var(--freisa-red)] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[var(--freisa-red-dark)]';
  const totalFacturaProcesada = typeof paquete.valorTotalFactura === 'number'
    ? paquete.valorTotalFactura
    : totalGuiasProcesadas;

  function updatePagoField<K extends keyof PagoPaquete>(field: K, value: PagoPaquete[K]) {
    setPaquete(prev => ({
      ...prev,
      pago: {
        ...(prev.pago ?? createEmptyPago()),
        [field]: value,
      },
    }));
  }

  function handleTipoPagoChange(tipo: FormaPagoPaquete) {
    setPaquete(prev => ({
      ...prev,
      pago: createPagoForType(tipo, totalGuiasProcesadas),
    }));
  }

  function handleTransferRouteSelectChange(nextDesde: string, nextHacia: string) {
    setTransferenciaDesdeSucursal(nextDesde);
    setTransferenciaHaciaSucursal(nextHacia);
    setPaquete(prev => ({
      ...prev,
      nombre: buildTransferRouteName(nextDesde, nextHacia),
      cliente: {
        ...(prev.cliente ?? createEmptyPaquete().cliente!),
        sucursal: nextDesde,
      },
    }));
  }

  function syncProcessedGuides(nextGuias: GuiaProcesada[]) {
    const firstGuia = nextGuias[0];
    const totalAcumulado = sumGuiasTotal(nextGuias);

    if (!firstGuia) {
      resetProcessedPdfState();
      return;
    }

    setPaquete(prev => ({
      ...prev,
      descripcion: buildGuiasDescripcion(nextGuias),
      guiaPdfUrl: firstGuia.guiaPdfUrl,
      guiaPdfNombreArchivo: firstGuia.guiaPdfNombreArchivo,
      ...(tipoEntrega === 'envio'
        ? {
            envioPdfUrl: '',
            envioPdfNombreArchivo: '',
          }
        : {}),
      valorTotalFactura: totalAcumulado,
      cliente: {
        ...buildProcessedCliente(firstGuia, sucursales, mostrandoTransferencia ? transferenciaDesdeSucursal : ''),
        tipoCliente: prev.cliente?.tipoCliente || '',
      },
    }));
  }

  function handleRemoveProcessedGuia(indexToRemove: number) {
    const nextGuias = guiasProcesadas.filter((_, index) => index !== indexToRemove);
    setGuiasProcesadas(nextGuias);
    syncProcessedGuides(nextGuias);
    setGuiaError('');
    setTransferenciaExcelError('');
  }

  function resetProcessedPdfState() {
    setGuiaError('');
    setTransferenciaExcelError('');
    setGuiasProcesadas([]);
    setUrlDestino('');
    setDestinoVerificado(null);
    setDestinoError('');
    setPaquete(prev => ({
      ...prev,
      descripcion: '',
      guiaPdfUrl: '',
      guiaPdfNombreArchivo: '',
      envioPdfUrl: '',
      envioPdfNombreArchivo: '',
      valorTotalFactura: undefined,
      pago: createEmptyPago(),
      cliente: {
        ...prev.cliente,
        nombre: '',
        numero: '',
        fechaDocumento: '',
        ciudad: '',
        sucursal: '',
        correoElectronico: '',
        direccion: '',
        telefono: '',
        tipoCliente: prev.cliente?.tipoCliente ?? '',
      },
    }));
  }

  function handleTipoEntregaChange(nextType: TipoEntregaViaje) {
    if (nextType === tipoEntrega) return;

    setTipoEntrega(nextType);
    if (nextType === 'transferencia') {
      setUrlDestino('');
      setDestinoVerificado(null);
      setDestinoError('');
      setPuntosIntermedios([]);
      setVerificandoPuntoId(null);
    }
    if (nextType !== 'transferencia') {
      setTransferenciaDesdeSucursal('');
      setTransferenciaHaciaSucursal('');
    }
    if (nextType !== 'envio') {
      setEnvioPdfDraft(null);
      setEnvioPdfError('');
    }
    setPaquete(prev => ({
      ...prev,
      pago: createEmptyPago(),
      ...(nextType !== 'envio'
        ? {
            envioPdfUrl: '',
            envioPdfNombreArchivo: '',
          }
        : {}),
    }));
    if (nextType !== 'envio') {
      setCompaniaTransporte('');
      setValorTotalEnvio(undefined);
    }

    if (guiasProcesadas.length > 0 || paquete.guiaPdfNombreArchivo) {
      resetProcessedPdfState();
    } else {
      setGuiaError('');
    }
  }

  async function handleGuiaPdf(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    setGuiaProcesando(true);
    setGuiaError('');
    try {
      const content = await fileToBase64(file);
      const result = await procesarGuiaPdf({
        fileName: file.name,
        content,
        tipoEntrega,
      });
      const nextGuias = [...guiasProcesadas, result];
      setGuiasProcesadas(nextGuias);
      syncProcessedGuides(nextGuias);
    } catch (err: unknown) {
      setGuiaError((err as { message?: string })?.message ?? 'No se pudo procesar la guía PDF.');
    } finally {
      setGuiaProcesando(false);
      event.target.value = '';
    }
  }

  async function handleTransferenciaExcel(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    setTransferenciaExcelProcesando(true);
    setTransferenciaExcelError('');
    try {
      const content = await fileToBase64(file);
      const result = await procesarTransferenciaExcel({
        fileName: file.name,
        content,
        tipoEntrega,
      });
      const nextGuias = [...guiasProcesadas, result];
      setGuiasProcesadas(nextGuias);
      syncProcessedGuides(nextGuias);
      setGuiaError('');
    } catch (err: unknown) {
      setTransferenciaExcelError((err as { message?: string })?.message ?? 'No se pudo procesar el archivo XLSX de transferencia.');
    } finally {
      setTransferenciaExcelProcesando(false);
      event.target.value = '';
    }
  }

  async function handleOpenEnvioPdfModal() {
    if (!paquete.nombre.trim()) return;

    setPreparandoEnvioPdf(true);
    setEnvioPdfError('');
    try {
      let nextLogoUrl = logoFacturaUrl;

      if (!nextLogoUrl) {
        const response = await listarParametrosPublicos();
        nextLogoUrl = (response.data.find(item => item.slug === 'logo_factura_url')?.valor || '').trim();
        setLogoFacturaUrl(nextLogoUrl);
      }

      setEnvioPdfDraft({
        logoUrl: nextLogoUrl,
        vendedorNombre: user?.profile?.razon_social?.trim() || user?.username?.trim() || 'Vendedor',
        entrega: '',
        cliente: '',
        identificacion: '',
        telefono: '',
        direccion: '',
        companiaTransporte: companiaTransporte.trim(),
      });
    } catch (err: unknown) {
      setEnvioPdfError((err as { message?: string })?.message ?? 'No se pudo preparar el PDF de envío.');
    } finally {
      setPreparandoEnvioPdf(false);
    }
  }

  async function handleConfirmarEnvioPdf() {
    const currentDraft = envioPdfDraft;
    const draftError = getEnvioPdfDraftError(envioPdfDraft);
    if (draftError || !currentDraft) {
      setEnvioPdfError(draftError ?? 'Debes completar todos los campos del PDF de envío.');
      return;
    }

    setGenerandoEnvioPdf(true);
    setEnvioPdfError('');
    try {
      const pdfFile = await buildEnvioPdfFile(currentDraft);
      const envioPdfUrl = await uploadFileToMinio(pdfFile, 'envios');
      setPaquete(prev => ({
        ...prev,
        envioPdfUrl,
        envioPdfNombreArchivo: pdfFile.name,
      }));
      downloadGeneratedFile(pdfFile);
      setEnvioPdfDraft(null);
    } catch (err: unknown) {
      setEnvioPdfError((err as { message?: string })?.message ?? 'No se pudo generar y guardar el PDF de envío.');
    } finally {
      setGenerandoEnvioPdf(false);
    }
  }

  // "Detalle/Logístico a Asignar" crece con el texto (hasta max-h) y se reajusta si cambia su ancho
  // (p. ej. en Envío comparte la fila con el aviso), para que el texto no quede oculto.
  useLayoutEffect(() => {
    const campo = detalleEntregaRef.current;
    if (!campo) return undefined;
    const ajustarAlto = () => {
      campo.style.height = 'auto';
      campo.style.height = `${campo.scrollHeight + campo.offsetHeight - campo.clientHeight}px`;
    };
    ajustarAlto();
    let anchoPrevio = campo.clientWidth;
    const observador = new ResizeObserver(() => {
      if (campo.clientWidth === anchoPrevio) return;
      anchoPrevio = campo.clientWidth;
      ajustarAlto();
    });
    observador.observe(campo);
    return () => observador.disconnect();
  }, [paquete.nombre, tipoEntrega, section]);

  function handlePaqueteNombreChange(nextNombre: string) {
    if (campoConError === 'paquete.nombre') setCampoConError('');
    setPaquete(prev => ({
      ...prev,
      nombre: nextNombre,
      ...(tipoEntrega === 'envio' && nextNombre !== prev.nombre
        ? {
            envioPdfUrl: '',
            envioPdfNombreArchivo: '',
          }
        : {}),
    }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const cliente = paquete.cliente;
    const paqueteParaGuardar = mostrandoTransferencia
      ? {
          ...paquete,
          cliente: {
            ...createEmptyPaquete().cliente!,
            ...(paquete.cliente ?? {}),
            correoElectronico: paquete.cliente?.correoElectronico?.trim() || 'example@gmail.com',
            tipoCliente: paquete.cliente?.tipoCliente || 'cliente_final',
          },
        }
      : paquete;

    if (!guiaPrincipal || !paquete.guiaPdfNombreArchivo) {
      showToast('Debes cargar y procesar al menos una guía PDF antes de crear la entrega.');
      return;
    }
    if (paquete.nombre.length > MAX_DETALLE_ENTREGA) {
      setCampoConError('paquete.nombre');
      showToast(`Detalle/Logístico a Asignar: máximo ${MAX_DETALLE_ENTREGA} caracteres (tiene ${paquete.nombre.length}).`);
      return;
    }
    if (!mostrandoTransferencia && (
      !cliente?.nombre?.trim()
      || !cliente.numero?.trim()
      || !cliente.fechaDocumento?.trim()
      || !cliente.ciudad?.trim()
      || !cliente.sucursal?.trim()
      || !cliente.correoElectronico?.trim()
      || !cliente.tipoCliente
    )) {
      showToast('Todos los campos del cliente son obligatorios antes de crear la entrega.');
      return;
    }
    if (!mostrandoTransferencia && !cliente?.telefono?.trim()) {
      showToast('Ingresa el teléfono o celular del cliente antes de crear la entrega.');
      return;
    }
    if (!mostrandoTransferencia && !urlDestino.trim()) {
      showToast('Ingresa la URL de Google Maps del destino.');
      return;
    }
    if (paymentRequiredForDeliveryType(tipoEntrega)) {
      const pagoError = validatePagoPaquete(paquete.pago) ?? validarMontoPorCobrar(paquete.pago, totalFacturaProcesada);
      if (pagoError) {
        showToast(pagoError);
        return;
      }
    }
    if (tipoEntrega === 'envio') {
      if (!companiaTransporte.trim()) {
        showToast('Debes indicar la compañía de transporte cuando el tipo de entrega es Envío.');
        return;
      }
      if (typeof valorTotalEnvio !== 'number' || valorTotalEnvio < 0) {
        showToast('Debes ingresar un valor total de envío válido.');
        return;
      }
      if (!paquete.envioPdfUrl?.trim() || !paquete.envioPdfNombreArchivo?.trim()) {
        showToast('Debes generar y guardar el PDF de envío antes de crear la entrega.');
        return;
      }
    }
    setSaving(true);
    try {
      const saveOptions = {
        guiaProcesada: {
          fileName: guiaPrincipal.guiaPdfNombreArchivo,
          numeroFactura: guiaPrincipal.cliente.numero,
          direccion: guiaPrincipal.extraccion.direccion || guiaPrincipal.cliente.direccion,
          extraccion: guiaPrincipal.extraccion,
        },
        guiasProcesadas: guiasProcesadas.map(guia => ({
          fileName: guia.guiaPdfNombreArchivo,
          numeroFactura: guia.cliente.numero,
          direccion: guia.extraccion.direccion || guia.cliente.direccion,
          extraccion: guia.extraccion,
        })),
      };
      const viaje = await saveViaje({
        id: crypto.randomUUID(),
        estado: 'pendiente',
        paquete: { ...paqueteParaGuardar, pago: pagoParaGuardar(paqueteParaGuardar.pago, totalFacturaProcesada) },
        tipoEntrega,
        companiaTransporte: tipoEntrega === 'envio' ? companiaTransporte.trim() : undefined,
        valorTotalEnvio: tipoEntrega === 'envio' ? valorTotalEnvio : undefined,
        urlDestino: mostrandoTransferencia ? undefined : urlDestino.trim(),
        puntosIntermedios: mostrandoTransferencia ? [] : puntosIntermediosRegistrados,
        tiempoEstimadoMinutos,
        coordenadas: [],
      }, saveOptions);

      setEntregasPagina(1);
      setSection('entregas');
      setTipoEntrega('entrega');
      setCompaniaTransporte('');
      setValorTotalEnvio(undefined);
      setPaquete(createEmptyPaquete());
      setGuiasProcesadas([]);
      setUrlDestino('');
      setPuntosIntermedios([]);
      setVerificandoPuntoId(null);
      setTiempoEstimadoMinutos(60);
      setGuiaError('');
      void refreshGeneral();
      dismissToast();
      showToast('La entrega se creó correctamente.', 'success');
    } catch (err: unknown) {
      const errorCampo = leerErrorValidacion(err);
      if (errorCampo) {
        setCampoConError(errorCampo.ruta);
        showToast(errorCampo.mensaje);
      } else {
        showToast((err as { message?: string })?.message ?? 'No se pudo crear la entrega.');
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleVerificarDestino() {
    const normalizedUrl = urlDestino.trim();

    if (!normalizedUrl) {
      setDestinoVerificado(null);
      setDestinoError('Ingresa una URL de Google Maps para verificar las coordenadas.');
      return;
    }

    setVerificandoDestino(true);
    setDestinoError('');
    try {
      const coordinates = await verificarDestinoGoogleMaps(normalizedUrl);
      setDestinoVerificado(coordinates);
    } catch (err: unknown) {
      setDestinoVerificado(null);
      setDestinoError((err as { message?: string })?.message ?? 'No se pudieron obtener las coordenadas del destino.');
    } finally {
      setVerificandoDestino(false);
    }
  }

  async function handleVerificarPuntoIntermedio(pointId: string) {
    const point = puntosIntermedios.find(candidate => candidate.id === pointId);
    if (!point?.urlDestino.trim()) {
      setPuntosIntermedios(current => current.map(candidate => (
        candidate.id === pointId
          ? { ...candidate, destinoVerificado: null, error: 'Ingresa una URL de Google Maps para este punto intermedio.' }
          : candidate
      )));
      return;
    }

    setVerificandoPuntoId(pointId);
    setPuntosIntermedios(current => current.map(candidate => (
      candidate.id === pointId
        ? { ...candidate, error: '' }
        : candidate
    )));

    try {
      const coordinates = await verificarDestinoGoogleMaps(point.urlDestino.trim());
      setPuntosIntermedios(current => current.map(candidate => (
        candidate.id === pointId
          ? { ...candidate, destinoVerificado: coordinates, error: '' }
          : candidate
      )));
    } catch (err: unknown) {
      setPuntosIntermedios(current => current.map(candidate => (
        candidate.id === pointId
          ? {
              ...candidate,
              destinoVerificado: null,
              error: (err as { message?: string })?.message ?? 'No se pudieron obtener las coordenadas de este punto intermedio.',
            }
          : candidate
      )));
    } finally {
      setVerificandoPuntoId(current => (current === pointId ? null : current));
    }
  }

  return (
    <PanelShell
      roleLabel="Vendedor"
      pageTitle="Panel de ventas"
      pageSubtitle="Vista operativa para crear entregas y seguir su estado hasta que el administrador las asigne."
      currentLabel={SECTION_COPY[section].label}
      currentDetail={SECTION_COPY[section].detail}
      userName={user?.profile?.razon_social ?? user?.username ?? 'Vendedor'}
      userMeta={user?.profile?.rol ?? 'VENDEDOR'}
      navItems={navItems}
      activeKey={section}
      onSelect={key => setSection(key as Section)}
      notifications={dashboardStats.notifications}
      onLogout={logout}
      aparienciaPersonalizable
    >
      {toast && (
        <div className="pointer-events-none fixed right-6 top-6 z-[80] flex w-full max-w-sm justify-end">
          <div
            className={`pointer-events-auto flex w-full items-start gap-3 rounded-[20px] border px-4 py-3 shadow-[0_20px_60px_rgba(28,25,23,0.18)] ${toast.tone === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-red-200 bg-white text-red-900'}`}
            role="status"
            aria-live="polite"
          >
            <span className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm ${toast.tone === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
              <i className={`fa-solid ${toast.tone === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'}`} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">{toast.tone === 'success' ? 'Proceso completado' : 'Revisa este dato'}</p>
              <p className="mt-1 text-sm leading-5 text-current/80">{toast.message}</p>
            </div>
            <button
              type="button"
              onClick={dismissToast}
              className="rounded-full p-1 text-current/60 transition hover:bg-black/5 hover:text-current"
              aria-label="Cerrar notificación"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        </div>
      )}

      {section === 'dashboard' && (
        <section className="space-y-6">
          <div className="grid gap-4 xl:grid-cols-4">
            {dashboardStats.summary.map(stat => (
              <DashboardStatCard
                key={stat.label}
                icon={stat.icon}
                label={stat.label}
                value={stat.value}
                trend={stat.trend}
                trendTone={stat.trendTone}
              />
            ))}
          </div>

          <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
            <WeeklyBarChart
              title="Envíos semanal"
              subtitle="Entregas creadas o movidas esta semana con la información ya disponible en ventas."
              data={dashboardStats.weeklySeries}
            />
            <ActivityFeed
              title="Actividad reciente"
              subtitle="Últimos movimientos de las entregas creadas por este rol."
              items={dashboardStats.recentActivity}
            />
          </div>
        </section>
      )}

          {section === 'entregas' && (
            <section className="space-y-6">
              <div className="flex flex-col gap-4">
                <div className="ui-filtros">
                  <div className="ui-filtro-busqueda">
                    <label className="ui-filtro-label">
                      Buscar entrega
                    </label>
                    <input
                      value={busqueda}
                      onChange={event => setBusqueda(event.target.value)}
                      className={inputCls}
                      placeholder="Nombre, factura, fecha, tipo, estado o conductor"
                    />
                  </div>
                  <div>
                    <label className="ui-filtro-label">
                      Estado
                    </label>
                    <select
                      value={estadoFiltro}
                      onChange={event => setEstadoFiltro(event.target.value as EstadoFiltro)}
                      className={inputCls}
                    >
                      <option value="todas">Todas</option>
                      <option value="pendiente">Asignadas</option>
                      <option value="en_curso">En camino</option>
                      <option value="finalizado">Entregadas</option>
                    </select>
                  </div>
                  <div>
                    <label className="ui-filtro-label">
                      Tipo de entrega
                    </label>
                    <select
                      value={tipoEntregaFiltro}
                      onChange={event => setTipoEntregaFiltro(event.target.value as TipoEntregaFiltro)}
                      className={inputCls}
                    >
                      <option value="todas">Todas</option>
                      <option value="entrega">Entrega</option>
                      <option value="envio">Envío</option>
                      <option value="transferencia">Transferencia</option>
                    </select>
                  </div>
                  <div>
                    <label className="ui-filtro-label">
                      Devolución
                    </label>
                    <select
                      value={devolucionFiltro}
                      onChange={event => setDevolucionFiltro(event.target.value as typeof devolucionFiltro)}
                      className={inputCls}
                    >
                      <option value="todas">Todas</option>
                      <option value="si">Solo devoluciones</option>
                      <option value="no">Sin devoluciones</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => setSection('crear')}
                    className={primaryButtonCls}
                  >
                    Nueva entrega
                  </button>
                </div>
              </div>

              {entregasTotalRegistros === 0 && !entregasCargando ? (
                <div className="rounded-[30px] border border-dashed border-stone-300 bg-white/70 p-12 text-center text-stone-500">
                  No hay entregas que coincidan con este filtro.
                </div>
              ) : (
                <div className="overflow-hidden rounded-[28px] border border-white/80 bg-white/90 shadow-[0_20px_50px_rgba(120,113,108,0.1)]">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-200 px-5 py-4">
                    <div>
                      <h3 className="text-lg font-semibold text-stone-900">Historial de entregas creadas</h3>
                      <p className="text-sm text-stone-500">Se muestran 10 registros por página para mantener la vista ligera.</p>
                    </div>
                    <p className="text-sm text-stone-500">{entregasTotalRegistros} registros</p>
                  </div>

                  {entregasCargando ? (
                    <div className="border-b border-stone-200 bg-blue-50 px-5 py-3 text-sm text-blue-700">Actualizando entregas...</div>
                  ) : null}

                  <div className="overflow-x-auto">
                    <table className="tabla-apilada min-w-full text-left text-sm text-stone-600">
                      <thead className="bg-stone-50 text-xs uppercase tracking-[0.16em] text-stone-500">
                        <tr>
                          <th className="px-5 py-3 font-semibold">Fecha de creación</th>
                          <th className="px-5 py-3 font-semibold">Entrega</th>
                          <th className="px-5 py-3 font-semibold">Conductor</th>
                          <th className="px-5 py-3 font-semibold">Tipo</th>
                          <th className="px-5 py-3 font-semibold">Estado</th>
                          <th className="px-5 py-3 font-semibold">Número de factura</th>
                          <th className="px-5 py-3 font-semibold">Forma de pago</th>
                          <th className="px-5 py-3 font-semibold text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entregas.map(viaje => {
                          const conductor = conductores.find(candidate => candidate.id === viaje.conductorId);
                          const invoiceNumbers = getTripInvoiceNumbers(viaje);
                          return (
                            <tr key={viaje.id} className="border-t border-stone-100 align-top">
                              <td data-label="Fecha de creación" className="px-5 py-4 whitespace-nowrap">{formatDateTime(viaje.createdAt)}</td>
                              <td data-label="Entrega" className="px-5 py-4 font-semibold text-stone-900">{viaje.paquete?.nombre || 'Entrega sin nombre'}</td>
                              <td data-label="Conductor" className="px-5 py-4">{conductor?.nombre || 'Pendiente de asignación'}</td>
                              <td data-label="Tipo" className="px-5 py-4">{formatDeliveryTypeLabel(viaje.tipoEntrega)}</td>
                              <td data-label="Estado" className="px-5 py-4">
                                <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${historialEstadoBadge(viaje)}`}>
                                  {historialEstadoLabel(viaje)}
                                </span>
                              </td>
                              <td data-label="Número de factura" className="px-5 py-4">
                                {invoiceNumbers.length > 0 ? (
                                  <div className="flex max-w-[260px] flex-wrap gap-2">
                                    {invoiceNumbers.map((invoiceNumber, index) => (
                                      <span
                                        key={`${viaje.id}-historial-factura-${index}`}
                                        className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-700"
                                      >
                                        {invoiceNumber}
                                      </span>
                                    ))}
                                  </div>
                                ) : '—'}
                              </td>
                              <td data-label="Forma de pago" className="px-5 py-4">{viaje.paquete?.pago?.tipo === 'completo' ? (viaje.paquete?.pago?.medioPago ? `Pagado (${formatPaymentLabel(viaje.paquete.pago.medioPago)})` : 'Pagado') : viaje.paquete?.pago?.tipo === 'parcial' ? `Por cobrar el saldo (${formatPaymentLabel(viaje.paquete?.pago?.tipoPagoParcial)})` : `Por cobrar (${formatPaymentLabel(viaje.paquete?.pago?.tipo)})`}</td>
                              <td data-label="" className="px-5 py-4 text-right">
                                <button
                                  type="button"
                                  onClick={() => setDetalleEntrega(viaje)}
                                  className="rounded-full bg-emerald-50 px-3 py-1.5 font-medium text-emerald-700 transition hover:bg-emerald-100"
                                >
                                  Ver detalles
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-3 border-t border-stone-200 px-5 py-4 text-sm text-stone-500">
                    <p>
                      Mostrando {Math.min((entregasPaginaActual - 1) * HISTORIAL_PAGE_SIZE + 1, entregasTotalRegistros)}-
                      {Math.min(entregasPaginaActual * HISTORIAL_PAGE_SIZE, entregasTotalRegistros)} de {entregasTotalRegistros}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setEntregasPagina(current => Math.max(1, current - 1))}
                        disabled={entregasPaginaActual === 1}
                        className="rounded-2xl border border-stone-200 px-3 py-2 font-medium text-stone-700 transition hover:bg-stone-100 disabled:opacity-50"
                      >
                        Anterior
                      </button>
                      <span>Página {entregasPaginaActual} de {entregasTotalPaginas}</span>
                      <button
                        type="button"
                        onClick={() => setEntregasPagina(current => Math.min(entregasTotalPaginas, current + 1))}
                        disabled={entregasPaginaActual === entregasTotalPaginas}
                        className="rounded-2xl border border-stone-200 px-3 py-2 font-medium text-stone-700 transition hover:bg-stone-100 disabled:opacity-50"
                      >
                        Siguiente
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}

          {section === 'crear' && (
            <section className="space-y-6">
              <div>
                <h3 className="text-2xl font-semibold text-stone-950">Crear entrega</h3>
                <p className="text-sm text-stone-600">La entrega quedará vinculada al vendedor autenticado y luego el administrador podrá asignarla a un conductor.</p>
              </div>

              <div className="rounded-[32px] border border-white/80 bg-white/85 p-6 shadow-[0_24px_60px_rgba(120,113,108,0.12)]">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="rounded-[28px] border border-stone-200 bg-white p-5">
                    <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-stone-500">Tipo de entrega</h3>
                    <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Modalidad</label>
                        <select
                          value={tipoEntrega}
                          onChange={event => handleTipoEntregaChange(event.target.value as TipoEntregaViaje)}
                          className={inputCls}
                        >
                          <option value="entrega">Entrega</option>
                          <option value="transferencia">Transferencia</option>
                          <option value="envio">Envío</option>
                        </select>
                      </div>

                      {tipoEntrega === 'envio' && (
                        <div>
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Valor total de envío</label>
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={valorTotalEnvio ?? ''}
                            onWheel={preventNumberInputWheelChange}
                            onChange={event => {
                              const parsed = parseOptionalNumber(event.target.value);
                              if (typeof parsed !== 'number') {
                                setValorTotalEnvio(undefined);
                                return;
                              }
                              setValorTotalEnvio(parsed);
                            }}
                            className={inputCls}
                            placeholder="0.00"
                          />
                        </div>
                      )}

                      {tipoEntrega === 'envio' && (
                        <div>
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Compañía transporte</label>
                          <input
                            value={companiaTransporte}
                            onChange={event => setCompaniaTransporte(event.target.value)}
                            className={inputCls}
                            placeholder="Ej: Servientrega"
                          />
                        </div>
                      )}

                      <div className="rounded-[18px] border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-700">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Modo de procesamiento PDF</p>
                        <p className="mt-1 font-semibold text-stone-900">{mostrandoTransferencia ? 'Transferencia' : 'Factura o guía'}</p>
                      </div>
                    </div>
                    <p className="mt-4 text-xs text-stone-500">
                      Selecciona primero el tipo de entrega. El PDF se procesa con esa modalidad y los datos extraídos se muestran debajo para que revises cliente o transferencia antes de crear la entrega.
                    </p>
                  </div>

                  <div className={mostrandoTransferencia ? 'grid items-start gap-4 xl:grid-cols-2' : ''}>
                    <div className="rounded-[28px] border border-stone-200 bg-stone-50/80 p-5">
                      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-stone-900">{mostrandoTransferencia ? 'Transferencia PDF' : 'Factura o guía PDF'}</p>
                          <p className="text-xs text-stone-500">
                            {mostrandoTransferencia
                              ? 'Carga el PDF de transferencia para extraer bodegas, transportista, chofer, descripción, total de productos y la tabla detallada.'
                              : 'Carga una o varias facturas o guías para extraer el cliente desde el primer documento, acumular descripciones y sumar el valor total procesado.'}
                          </p>
                        </div>
                      </div>
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={handleGuiaPdf}
                        className="mt-4 block w-full text-sm text-stone-500 file:mr-3 file:rounded-2xl file:border-0 file:bg-stone-950 file:px-3 file:py-2 file:text-white hover:file:bg-stone-800"
                      />
                      {guiaProcesando && (
                        <p className="mt-3 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                          {mostrandoTransferencia
                            ? 'Procesando transferencia PDF y separando sus datos de bodega y productos...'
                            : 'Procesando factura o guía PDF y agregando el documento al acumulado...'}
                        </p>
                      )}
                      {guiaError && (
                        <p className="mt-3 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                          {guiaError}
                        </p>
                      )}
                      {guiasProcesadas.length > 0 && !guiaProcesando && (
                        <p className="mt-3 text-sm text-stone-600">
                          {guiasProcesadas.length === 1 ? 'Archivo procesado' : 'Archivos procesados'}:{' '}
                          <span className="font-medium text-stone-900">{guiasProcesadas.map(guia => guia.guiaPdfNombreArchivo).join(', ')}</span>
                        </p>
                      )}
                      {guiasProcesadas.length > 0 && !guiaProcesando && (
                        <div className="mt-4 rounded-[24px] border border-stone-200 bg-white px-4 py-4">
                          <p className="text-sm font-semibold text-stone-900">Guías procesadas</p>
                          <p className="mt-1 text-xs text-stone-500">
                            {mostrandoTransferencia
                              ? 'Los datos base se cargan desde la primera transferencia y debajo puedes revisar el detalle extraído antes de crear la entrega.'
                              : 'Los datos del cliente se cargan solo desde la primera guía. Cada factura nueva suma su total y agrega su bloque a la descripción del paquete.'}
                          </p>
                          <div className={mostrandoTransferencia ? 'mt-4 grid gap-3 md:grid-cols-3' : 'mt-4 grid gap-3 md:grid-cols-2'}>
                            <div className="rounded-2xl bg-stone-50 px-3 py-3 text-sm text-stone-700">
                              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">{mostrandoTransferencia ? 'Documentos procesados' : 'Facturas procesadas'}</p>
                              <p className="mt-1">{guiasProcesadas.length}</p>
                            </div>
                            {mostrandoTransferencia && (
                              <div className="rounded-2xl bg-stone-50 px-3 py-3 text-sm text-stone-700">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Total ítems</p>
                                <p className="mt-1">{typeof totalItemsTransferencia === 'number' ? totalItemsTransferencia : '—'}</p>
                              </div>
                            )}
                            <div className="rounded-2xl bg-stone-50 px-3 py-3 text-sm text-stone-700">
                              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">{mostrandoTransferencia ? 'Total unidades' : 'Valor total factura'}</p>
                              <p className="mt-1">{mostrandoTransferencia ? (typeof totalProductosTransferencia === 'number' ? totalProductosTransferencia : '—') : formatMoney(totalFacturaProcesada)}</p>
                            </div>
                          </div>

                          <div className="mt-4 space-y-4">
                            {guiasProcesadas.map((guia, index) => {
                              const transferencia = guia.extraccion.transferencia;
                              const esTransferenciaExcel = guia.extraccion.tipoDocumento === 'transferencia' && isSpreadsheetGuide(guia.guiaPdfNombreArchivo);
                              return (
                                <div key={`${guia.guiaPdfNombreArchivo}-${index}`} className="rounded-[20px] border border-stone-200 bg-stone-50/60 p-4">
                                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                    <div>
                                      <p className="text-sm font-semibold text-stone-900">{guia.guiaPdfNombreArchivo}</p>
                                      <p className="text-xs uppercase tracking-[0.16em] text-stone-500">{guia.extraccion.tipoDocumento === 'transferencia' ? (esTransferenciaExcel ? 'Transferencia Excel detectada' : 'Transferencia detectada') : 'Factura detectada'}</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <p className="text-sm text-stone-600">{guia.extraccion.tipoDocumento === 'transferencia' ? (transferencia?.numero || 'Sin número') : formatFacturaLabel(guia.cliente.numero)}</p>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveProcessedGuia(index)}
                                        className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-100"
                                      >
                                        Eliminar archivo
                                      </button>
                                    </div>
                                  </div>

                                  {guia.extraccion.tipoDocumento === 'transferencia' && transferencia ? (
                                    esTransferenciaExcel ? (
                                      <div className="mt-4 space-y-4">
                                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                          <div className="rounded-2xl bg-white px-3 py-3 text-sm text-stone-700"><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Fecha</p><p className="mt-1">{transferencia.fecha || '—'}</p></div>
                                          <div className="rounded-2xl bg-white px-3 py-3 text-sm text-stone-700"><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Bodega origen</p><p className="mt-1">{transferencia.bodegaOrigen || '—'}</p></div>
                                          <div className="rounded-2xl bg-white px-3 py-3 text-sm text-stone-700"><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Bodega destino</p><p className="mt-1">{transferencia.bodegaDestino || '—'}</p></div>
                                          <div className="rounded-2xl bg-white px-3 py-3 text-sm text-stone-700"><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Total unidades</p><p className="mt-1">{typeof transferencia.totalUnidades === 'number' ? transferencia.totalUnidades : (typeof transferencia.totalProductos === 'number' ? transferencia.totalProductos : '—')}</p></div>
                                        </div>

                                        <div className="rounded-2xl border border-dashed border-stone-200 bg-white px-4 py-4 text-sm text-stone-600">
                                          El archivo XLSX ya fue procesado y sus datos se cargaron en el formulario. No se muestra el detalle completo para mantener esta vista más ligera.
                                        </div>
                                      </div>
                                    ) : (
                                    <div className="mt-4 space-y-4">
                                      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                        <div className="rounded-2xl bg-white px-3 py-3 text-sm text-stone-700"><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Fecha</p><p className="mt-1">{transferencia.fecha || '—'}</p></div>
                                        <div className="rounded-2xl bg-white px-3 py-3 text-sm text-stone-700"><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Bodega origen</p><p className="mt-1">{transferencia.bodegaOrigen || '—'}</p></div>
                                        <div className="rounded-2xl bg-white px-3 py-3 text-sm text-stone-700"><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Bodega destino</p><p className="mt-1">{transferencia.bodegaDestino || '—'}</p></div>
                                        <div className="rounded-2xl bg-white px-3 py-3 text-sm text-stone-700"><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Total ítems</p><p className="mt-1">{typeof transferencia.totalItems === 'number' ? transferencia.totalItems : transferencia.items.length}</p></div>
                                        <div className="rounded-2xl bg-white px-3 py-3 text-sm text-stone-700"><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Total unidades</p><p className="mt-1">{typeof transferencia.totalUnidades === 'number' ? transferencia.totalUnidades : (typeof transferencia.totalProductos === 'number' ? transferencia.totalProductos : '—')}</p></div>
                                        <div className="rounded-2xl bg-white px-3 py-3 text-sm text-stone-700"><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Transportista</p><p className="mt-1">{transferencia.transportista || '—'}</p></div>
                                        <div className="rounded-2xl bg-white px-3 py-3 text-sm text-stone-700"><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Vehículo</p><p className="mt-1">{transferencia.vehiculo || '—'}</p></div>
                                        <div className="rounded-2xl bg-white px-3 py-3 text-sm text-stone-700"><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Chofer</p><p className="mt-1">{transferencia.chofer || '—'}</p></div>
                                        <div className="rounded-2xl bg-white px-3 py-3 text-sm text-stone-700"><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Correo</p><p className="mt-1 break-all">{guia.cliente.correoElectronico || '—'}</p></div>
                                      </div>

                                      {transferencia.descripcion && (
                                        <div className="rounded-2xl bg-white px-4 py-4 text-sm text-stone-700">
                                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Descripción</p>
                                          <p className="mt-2 whitespace-pre-wrap">{transferencia.descripcion}</p>
                                        </div>
                                      )}

                                      <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white">
                                        <table className="min-w-full text-left text-sm text-stone-600">
                                          <thead className="bg-stone-100 text-[11px] uppercase tracking-[0.16em] text-stone-500">
                                            <tr>
                                              <th className="px-3 py-2 font-semibold">Código</th>
                                              <th className="px-3 py-2 font-semibold">Código comercial</th>
                                              <th className="px-3 py-2 font-semibold">M. repuesto</th>
                                              <th className="px-3 py-2 font-semibold">Producto</th>
                                              <th className="px-3 py-2 font-semibold text-right">Cantidad</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {transferencia.items.length > 0 ? transferencia.items.map((item, itemIndex) => (
                                              <tr key={`${item.codigo}-${itemIndex}`} className="border-t border-stone-100">
                                                <td className="px-3 py-2">{item.codigo}</td>
                                                <td className="px-3 py-2">{item.codigoComercial || '—'}</td>
                                                <td className="px-3 py-2">{item.marcaRepuesto || '—'}</td>
                                                <td className="px-3 py-2">{item.producto}</td>
                                                <td className="px-3 py-2 text-right">{item.cantidad ?? '—'}</td>
                                              </tr>
                                            )) : (
                                              <tr>
                                                <td colSpan={5} className="px-3 py-4 text-center text-stone-500">No se detectaron productos en esta transferencia.</td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                    )
                                  ) : (
                                    <div className="mt-4 space-y-4">
                                      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                        <div className="rounded-2xl bg-white px-3 py-3 text-sm text-stone-700"><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Cliente</p><p className="mt-1">{guia.cliente.nombre || '—'}</p></div>
                                        <div className="rounded-2xl bg-white px-3 py-3 text-sm text-stone-700"><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Número</p><p className="mt-1">{guia.cliente.numero || '—'}</p></div>
                                        <div className="rounded-2xl bg-white px-3 py-3 text-sm text-stone-700"><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Correo</p><p className="mt-1 break-all">{guia.cliente.correoElectronico || '—'}</p></div>
                                        <div className="rounded-2xl bg-white px-3 py-3 text-sm text-stone-700"><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Total</p><p className="mt-1">{formatMoney(guia.extraccion.total)}</p></div>
                                      </div>

                                      <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white">
                                        <table className="min-w-full text-left text-sm text-stone-600">
                                          <thead className="bg-stone-100 text-[11px] uppercase tracking-[0.16em] text-stone-500">
                                            <tr>
                                              <th className="px-3 py-2 font-semibold">Código</th>
                                              <th className="px-3 py-2 font-semibold">Descripción</th>
                                              <th className="px-3 py-2 font-semibold text-right">Cantidad</th>
                                              <th className="px-3 py-2 font-semibold text-right">Precio unit.</th>
                                              <th className="px-3 py-2 font-semibold text-right">Precio total</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {guia.extraccion.items.length > 0 ? guia.extraccion.items.map((item, itemIndex) => (
                                              <tr key={`${item.codigo}-${itemIndex}`} className="border-t border-stone-100 align-top">
                                                <td className="px-3 py-2 font-medium text-stone-900">{item.codigo}</td>
                                                <td className="px-3 py-2">{item.descripcion}</td>
                                                <td className="px-3 py-2 text-right">{typeof item.cantidad === 'number' ? formatMoney(item.cantidad) : '—'}</td>
                                                <td className="px-3 py-2 text-right">{formatMoney(item.precioUnitario)}</td>
                                                <td className="px-3 py-2 text-right font-semibold text-stone-900">{formatMoney(item.precioTotal)}</td>
                                              </tr>
                                            )) : (
                                              <tr>
                                                <td colSpan={5} className="px-3 py-4 text-center text-stone-500">No se detectaron productos en esta factura o guía.</td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    {mostrandoTransferencia && (
                      <div className="rounded-[28px] border border-stone-200 bg-stone-50/80 p-5">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <p className="text-sm font-semibold text-stone-900">Transferencia Excel XLSX</p>
                            <p className="text-xs text-stone-500">
                              Carga el archivo XLSX exportado desde Excel para extraer cabecera, bodegas y toda la tabla de productos sin tocar el parser PDF existente.
                            </p>
                          </div>
                        </div>
                        <input
                          type="file"
                          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                          onChange={handleTransferenciaExcel}
                          className="mt-4 block w-full text-sm text-stone-500 file:mr-3 file:rounded-2xl file:border-0 file:bg-stone-950 file:px-3 file:py-2 file:text-white hover:file:bg-stone-800"
                        />
                        {transferenciaExcelProcesando && (
                          <p className="mt-3 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                            Procesando archivo XLSX de transferencia y preparando la tabla completa de productos...
                          </p>
                        )}
                        {transferenciaExcelError && (
                          <p className="mt-3 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                            {transferenciaExcelError}
                          </p>
                        )}
                        <div className="mt-4 rounded-2xl border border-dashed border-stone-200 bg-white px-4 py-4 text-xs leading-5 text-stone-500">
                          Busca automáticamente Número, Fecha, Bodega Origen, Bodega Destino, Transportista, Vehículo, Chofer y la tabla Código/Código Comercial/M. Repuesto/Producto/Cant.
                        </div>
                      </div>
                    )}
                  </div>

                  {tipoEntrega === 'transferencia' && (
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
                          De:
                        </label>
                        <select
                          value={transferenciaDesdeSucursal}
                          onChange={event => handleTransferRouteSelectChange(event.target.value, transferenciaHaciaSucursal)}
                          className={inputCls}
                        >
                          <option value="">Selecciona sucursal origen</option>
                          {sucursales.map(sucursal => (
                            <option key={`desde-${sucursal.id}`} value={sucursal.nombre}>
                              {sucursal.nombre}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
                          Hacia:
                        </label>
                        <select
                          value={transferenciaHaciaSucursal}
                          onChange={event => handleTransferRouteSelectChange(transferenciaDesdeSucursal, event.target.value)}
                          className={inputCls}
                        >
                          <option value="">Selecciona sucursal destino</option>
                          {sucursales.map(sucursal => (
                            <option key={`hacia-${sucursal.id}`} value={sucursal.nombre}>
                              {sucursal.nombre}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  <div className="grid gap-4">
                    <div>
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <label htmlFor="detalle-entrega" className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
                          Detalle/Logístico a Asignar
                        </label>
                        {tipoEntrega === 'envio' && (
                          <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-800">
                            Obligatorio
                          </span>
                        )}
                      </div>
                      <div className={tipoEntrega === 'envio' ? 'grid gap-3 xl:grid-cols-[minmax(0,1fr)_320px]' : 'block'}>
                        <div className="min-w-0">
                          <textarea
                            id="detalle-entrega"
                            rows={2}
                            value={paquete.nombre}
                            // Se guarda y se muestra en una sola línea: los saltos de línea pasan a espacios.
                            ref={detalleEntregaRef}
                            onChange={event => handlePaqueteNombreChange(event.target.value.replace(/\s*\n\s*/g, ' '))}
                            onKeyDown={event => { if (event.key === 'Enter') event.preventDefault(); }}
                            required
                            maxLength={MAX_DETALLE_ENTREGA}
                            aria-invalid={campoConError === 'paquete.nombre'}
                            aria-describedby="detalle-entrega-restantes"
                            className={`${inputCls} max-h-52 w-full resize-y overflow-y-auto ${campoConError === 'paquete.nombre' ? 'border-rose-400 bg-rose-50/60 ring-4 ring-rose-100' : ''}`}
                            placeholder={tipoEntrega === 'transferencia' ? 'Ej: Matriz/Bodega Norte' : 'Ej: R1 · indicaciones para el logístico'}
                          />
                          {(() => {
                            const restantes = MAX_DETALLE_ENTREGA - paquete.nombre.length;
                            const cerca = restantes <= AVISO_DETALLE_RESTANTES;
                            return (
                              <p
                                id="detalle-entrega-restantes"
                                aria-live="polite"
                                className={`mt-1.5 text-xs font-semibold ${cerca || campoConError === 'paquete.nombre' ? 'text-rose-600' : 'text-emerald-600'}`}
                              >
                                {restantes === 1 ? 'Te queda 1 carácter' : `Te quedan ${restantes} caracteres`}
                              </p>
                            );
                          })()}
                        </div>
                        {tipoEntrega === 'envio' && (
                          <div className="rounded-[18px] border border-amber-200 bg-amber-50/90 px-4 py-3 text-amber-900 shadow-sm">
                            <div className="flex items-start gap-3">
                              <i className="fa-solid fa-triangle-exclamation pt-0.5 text-sm text-amber-600" aria-hidden="true" />
                              <p className="text-xs font-semibold leading-5">
                                Este campo es obligatorio para generar el PDF de envío y poder guardarlo, registrando los datos del <span className="font-black">CLIENTE</span>.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                      {tipoEntrega === 'envio' && (
                        <div className="mt-3 flex justify-start">
                          <button
                            type="button"
                            onClick={() => void handleOpenEnvioPdfModal()}
                            disabled={!paquete.nombre.trim() || !guiaPrincipal || preparandoEnvioPdf}
                            className="inline-flex items-center gap-2 rounded-[14px] border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <i className="fa-solid fa-file-pdf" aria-hidden="true" />
                            <span>{preparandoEnvioPdf ? 'Preparando PDF...' : 'Generar PDF envío'}</span>
                          </button>
                        </div>
                      )}
                      {tipoEntrega === 'envio' && !guiaPrincipal && (
                        <p className="mt-2 text-xs text-stone-500">
                          Procesa primero la factura o guía para completar automáticamente CLIENTE, CI y teléfono en el PDF de envío.
                        </p>
                      )}
                      {tipoEntrega === 'envio' && envioPdfPendiente && guiaPrincipal && (
                        <p className="mt-2 text-xs font-semibold text-red-600">
                          Debes generar y guardar el PDF de envío antes de crear la entrega.
                        </p>
                      )}
                      {tipoEntrega === 'envio' && paquete.envioPdfUrl && (
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-stone-600">
                          <span className="font-medium text-stone-700">PDF de envío guardado:</span>
                          <a
                            href={paquete.envioPdfUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="font-semibold text-red-700 underline decoration-red-200 underline-offset-2"
                          >
                            {paquete.envioPdfNombreArchivo || 'Abrir PDF de envío'}
                          </a>
                        </div>
                      )}
                      {tipoEntrega === 'transferencia' && (
                        <p className="mt-2 text-xs text-stone-500">
                          Los selects solo completan esta ruta automáticamente. Puedes editar el valor antes de guardar y esa información no se conserva por separado.
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
                      Descripcion
                    </label>
                    <textarea
                      value={paquete.descripcion}
                      onChange={event => setPaquete(prev => ({ ...prev, descripcion: event.target.value }))}
                      required
                      rows={4}
                      className={inputCls}
                      placeholder="Describe lo que debe entregarse"
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    {!mostrandoTransferencia && (
                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
                          URL destino Google Maps
                        </label>
                        <div className="space-y-4">
                          <div className={isDestinationHistoryOpen ? 'relative z-30' : 'relative'}>
                            <input
                              value={urlDestino}
                              onChange={event => setUrlDestino(event.target.value)}
                              onFocus={() => {
                                if (destinationHistoryEnabled && historicalDestinationUrls.length > 0) {
                                  setIsDestinationHistoryOpen(true);
                                }
                              }}
                              onBlur={() => {
                                window.setTimeout(() => {
                                  setIsDestinationHistoryOpen(false);
                                }, 120);
                              }}
                              required
                              className={`${inputCls} ${destinationHistoryEnabled && historicalDestinationUrls.length > 0 ? 'pr-12' : ''}`}
                              placeholder="https://maps.google.com/..."
                            />
                            {destinationHistoryEnabled && historicalDestinationUrls.length > 0 && (
                              <button
                                type="button"
                                onMouseDown={event => {
                                  event.preventDefault();
                                  setIsDestinationHistoryOpen(current => !current);
                                }}
                                className="absolute inset-y-0 right-3 my-auto flex h-8 w-8 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-500 transition hover:border-stone-300 hover:text-stone-700"
                                aria-label="Mostrar ubicaciones previas del cliente"
                              >
                                <i className={`fa-solid fa-chevron-${isDestinationHistoryOpen ? 'up' : 'down'} text-xs`} />
                              </button>
                            )}
                            {tipoEntrega === 'entrega' && historicalDestinationUrls.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setIsDestinationHistoryOpen(current => !current)}
                                className="mt-2 inline-flex items-center gap-2 rounded-full border border-stone-200 bg-stone-50 px-3 py-1.5 text-xs font-semibold text-stone-700 transition hover:border-stone-300 hover:bg-stone-100"
                              >
                                <span>{isDestinationHistoryOpen ? 'Ocultar' : 'Ver'} ubicaciones previas</span>
                                <span className="inline-flex min-w-6 items-center justify-center rounded-full bg-white px-2 py-0.5 text-[11px] text-stone-600">
                                  {historicalDestinationUrls.length}
                                </span>
                              </button>
                            )}
                            {destinationHistoryEnabled && historicalDestinationUrls.length > 0 && isDestinationHistoryOpen && (
                              <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-[20px] border border-stone-200 bg-white shadow-[0_18px_40px_rgba(28,25,23,0.12)]">
                                <div className="border-b border-stone-100 px-4 py-3">
                                  <p className="text-sm font-semibold text-stone-900">Ubicaciones previas del cliente</p>
                                  <p className="text-xs text-stone-500">Selecciona una ubicación registrada o escribe una nueva en el mismo campo. La numeración es solo visual y no modifica la URL guardada.</p>
                                </div>
                                <div className="max-h-64 overflow-y-auto p-2">
                                  {historicalDestinationUrls.map((url, index) => {
                                    const selected = urlDestino.trim() === url;
                                    const displayIndex = index + 1;
                                    return (
                                      <button
                                        key={`${url}-${index}`}
                                        type="button"
                                        onMouseDown={event => {
                                          event.preventDefault();
                                          setUrlDestino(url);
                                          setIsDestinationHistoryOpen(false);
                                        }}
                                        className={`mb-1 w-full rounded-2xl border px-3 py-2 text-left text-xs transition last:mb-0 ${selected ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300 hover:bg-stone-100'}`}
                                      >
                                        <span className="mr-2 inline-flex min-w-6 items-center justify-center rounded-full bg-stone-100 px-1.5 py-0.5 text-[11px] font-semibold text-stone-600">
                                          {displayIndex}
                                        </span>
                                        <span>{url}</span>
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-3">
                            <button
                              type="button"
                              onClick={() => void handleVerificarDestino()}
                              disabled={verificandoDestino}
                              className="rounded-[14px] border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100 disabled:opacity-60"
                            >
                              {verificandoDestino ? 'Verificando coordenadas...' : 'Verificar coordenadas'}
                            </button>
                            <p className="text-xs text-stone-500">
                              {tipoEntrega === 'entrega'
                                ? 'Al ingresar o seleccionar una URL válida el mapa se actualiza automáticamente. También puedes revalidarla manualmente aquí.'
                                : 'Acepta URLs de escritorio con @lat,lng y enlaces de celular que redirigen antes de extraer las coordenadas.'}
                            </p>
                          </div>
                          {destinoVerificado && (
                            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                              Coordenadas detectadas: lat {destinoVerificado.latitud}, lng {destinoVerificado.longitud}
                            </div>
                          )}
                          {destinoError && (
                            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                              {destinoError}
                            </div>
                          )}

                          <div className="rounded-[20px] border border-orange-200 bg-orange-50/60 p-4">
                            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                              <div>
                                <p className="text-sm font-semibold text-stone-900">Puntos intermedios</p>
                                <p className="text-xs text-stone-500">Puedes agregar paradas opcionales para mostrarlas en el mapa. El punto final sigue siendo obligatorio.</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => setPuntosIntermedios(current => [...current, createEmptyPuntoIntermedio()])}
                                className="rounded-[14px] border border-orange-200 bg-white px-4 py-2 text-sm font-semibold text-orange-700 transition hover:bg-orange-100"
                              >
                                Añadir punto intermedio
                              </button>
                            </div>

                            {puntosIntermedios.length === 0 ? (
                              <div className="mt-4 rounded-2xl border border-dashed border-orange-200 bg-white/70 px-4 py-4 text-sm text-stone-500">
                                No has agregado puntos intermedios. Si la ruta tiene paradas adicionales, agrégalas aquí.
                              </div>
                            ) : (
                              <div className="mt-4 space-y-3">
                                {puntosIntermedios.map((point, index) => {
                                  const puntoVerificado = Boolean(point.destinoVerificado);
                                  return (
                                    <div
                                      key={point.id}
                                      className={`rounded-2xl border bg-white p-4 ${puntoVerificado ? 'border-emerald-200 bg-emerald-50/35' : 'border-orange-100'}`}
                                    >
                                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                        <div>
                                          <p className="text-sm font-semibold text-stone-900">Punto intermedio {index + 1}</p>
                                          <p className="text-xs text-stone-500">Registra otra URL de Google Maps para mostrar una parada adicional.</p>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => setPuntosIntermedios(current => current.filter(candidate => candidate.id !== point.id))}
                                          className="rounded-[14px] border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 transition hover:bg-rose-100"
                                        >
                                          Eliminar punto
                                        </button>
                                      </div>

                                      <div className="mt-3 space-y-3">
                                        <input
                                          value={point.urlDestino}
                                          onChange={event => setPuntosIntermedios(current => current.map(candidate => (
                                            candidate.id === point.id
                                              ? { ...candidate, urlDestino: event.target.value, destinoVerificado: null, error: '' }
                                              : candidate
                                          )))}
                                          className={inputCls}
                                          placeholder="https://maps.google.com/..."
                                        />
                                        <div className="flex flex-wrap items-center gap-3">
                                          <button
                                            type="button"
                                            onClick={() => void handleVerificarPuntoIntermedio(point.id)}
                                            disabled={verificandoPuntoId === point.id}
                                            className={`rounded-[14px] border px-4 py-2 text-sm font-semibold transition disabled:opacity-60 ${puntoVerificado ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100'}`}
                                          >
                                            {verificandoPuntoId === point.id ? 'Verificando punto...' : 'Verificar punto'}
                                          </button>
                                          {point.destinoVerificado && (
                                            <p className="text-xs font-medium text-emerald-700">
                                              Coordenadas detectadas: lat {point.destinoVerificado.latitud}, lng {point.destinoVerificado.longitud}
                                            </p>
                                          )}
                                        </div>
                                        {point.error && (
                                          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                                            {point.error}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
                        Tiempo estimado (minutos)
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={tiempoEstimadoMinutos}
                        onWheel={preventNumberInputWheelChange}
                        onChange={event => setTiempoEstimadoMinutos(Number(event.target.value) || 0)}
                        required
                        className={inputCls}
                      />
                      {tipoEntrega === 'entrega' && (
                        <div className="mt-4 space-y-3">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Mapa del destino</p>
                            <p className="mt-1 text-xs text-stone-500">Vista OpenStreetMap actualizada con la URL seleccionada o ingresada.</p>
                          </div>
                          {previewDestinoCoordinate ? (
                            <MapaAdmin
                              coordenadas={[]}
                              coordenadaFin={previewDestinoCoordinate}
                              puntosIntermedios={[]}
                            />
                          ) : (
                            <div className="rounded-[20px] border border-dashed border-stone-300 bg-stone-50/80 px-4 py-8 text-sm text-stone-500">
                              {urlDestino.trim()
                                ? (verificandoDestino
                                  ? 'Actualizando el mapa del destino...'
                                  : 'No hay coordenadas disponibles todavía para mostrar el mapa con esta URL.')
                                : 'Selecciona o escribe una URL de Google Maps para ver el destino en el mapa.'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {!mostrandoTransferencia && (
                  <div className="rounded-[28px] border border-stone-200 bg-white p-5">
                    <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-stone-500">Cliente</h3>
                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Cliente</label>
                        <input
                          value={paquete.cliente?.nombre ?? ''}
                          onChange={event => setPaquete(prev => ({
                            ...prev,
                            cliente: { ...prev.cliente!, nombre: event.target.value },
                          }))}
                          required
                          className="w-full rounded-2xl border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100"
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Numero Factura</label>
                        <input
                          value={paquete.cliente?.numero ?? ''}
                          onChange={event => setPaquete(prev => ({
                            ...prev,
                            cliente: { ...prev.cliente!, numero: event.target.value },
                          }))}
                          required
                          className="w-full rounded-2xl border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100"
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Fecha</label>
                        <input
                          value={paquete.cliente?.fechaDocumento ?? ''}
                          onChange={event => setPaquete(prev => ({
                            ...prev,
                            cliente: { ...prev.cliente!, fechaDocumento: event.target.value },
                          }))}
                          required
                          className="w-full rounded-2xl border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100"
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Ciudad</label>
                        <input
                          value={paquete.cliente?.ciudad ?? ''}
                          onChange={event => setPaquete(prev => ({
                            ...prev,
                            cliente: { ...prev.cliente!, ciudad: event.target.value },
                          }))}
                          required
                          className="w-full rounded-2xl border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100"
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Sucursal</label>
                        <select
                          value={paquete.cliente?.sucursal ?? ''}
                          onChange={event => setPaquete(prev => ({
                            ...prev,
                            cliente: { ...prev.cliente!, sucursal: event.target.value },
                          }))}
                          required
                          className="w-full rounded-2xl border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100"
                        >
                          <option value="">Selecciona una sucursal</option>
                          {sucursales.map(sucursal => (
                            <option key={sucursal.id} value={sucursal.nombre}>
                              {sucursal.nombre}
                            </option>
                          ))}
                        </select>
                        {paquete.cliente?.sucursal && (
                          <p className="mt-2 text-xs text-stone-500">
                            {sucursales.find(sucursal => sucursal.nombre === paquete.cliente?.sucursal)?.direccion || 'Sucursal seleccionada'}
                            {sucursales.find(sucursal => sucursal.nombre === paquete.cliente?.sucursal)?.telefono
                              ? ` · ${sucursales.find(sucursal => sucursal.nombre === paquete.cliente?.sucursal)?.telefono}`
                              : ''}
                          </p>
                        )}
                        {!sucursales.length && (
                          <p className="mt-2 text-xs text-amber-700">
                            No hay sucursales registradas. Pide al administrador que cree al menos una para continuar.
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Correo electronico</label>
                        <input
                          type="email"
                          value={paquete.cliente?.correoElectronico ?? ''}
                          onChange={event => setPaquete(prev => ({
                            ...prev,
                            cliente: { ...prev.cliente!, correoElectronico: event.target.value },
                          }))}
                          required
                          className="w-full rounded-2xl border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100"
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Teléfono / celular <span className="text-[var(--freisa-red)]">*</span></label>
                        <input
                          type="tel"
                          inputMode="tel"
                          autoComplete="tel"
                          maxLength={50}
                          value={paquete.cliente?.telefono ?? ''}
                          onChange={event => setPaquete(prev => ({
                            ...prev,
                            cliente: { ...prev.cliente!, telefono: event.target.value },
                          }))}
                          placeholder="Ej: 0991234567"
                          className="w-full rounded-2xl border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100"
                        />
                        <p className="mt-1.5 text-xs text-stone-500">Obligatorio. Se carga del PDF cuando trae teléfono o celular; puedes corregirlo.</p>
                      </div>
                    </div>
                    <div className="mt-5 space-y-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Tipo de cliente</p>
                        <p className="mt-1 text-xs text-stone-500">Selecciona una sola opción para guardar esta entrega.</p>
                      </div>
                      <div className="grid gap-3 md:grid-cols-2">
                        {CLIENT_TYPE_OPTIONS.map(option => {
                          const selected = paquete.cliente?.tipoCliente === option.value;
                          return (
                            <label
                              key={option.value}
                              className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-sm transition ${selected ? 'border-teal-500 bg-teal-50 text-teal-900' : 'border-stone-300 bg-stone-50 text-stone-700 hover:border-stone-400'}`}
                            >
                              <input
                                type="radio"
                                name="tipo-cliente"
                                value={option.value}
                                checked={selected}
                                onChange={event => setPaquete(prev => ({
                                  ...prev,
                                  cliente: { ...prev.cliente!, tipoCliente: event.target.value as Exclude<TipoCliente, ''> },
                                }))}
                                className="h-4 w-4 border-stone-300 text-teal-600 focus:ring-teal-500"
                              />
                              <span className="font-medium">{option.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  )}

                  <div className="rounded-[28px] border border-stone-200 bg-white p-5">
                    <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-stone-500">Resumen procesado</h3>
                    <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                      {mostrandoTransferencia && (
                        <div className="rounded-[18px] border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-700">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">Total ítems</p>
                          <p className="mt-1 font-semibold text-stone-900">{typeof totalItemsTransferencia === 'number' ? totalItemsTransferencia : '—'}</p>
                        </div>
                      )}
                      <div className="rounded-[18px] border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-700">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">{mostrandoTransferencia ? 'Total unidades' : 'Valor total factura'}</p>
                        <p className="mt-1 font-semibold text-stone-900">{mostrandoTransferencia ? (typeof totalProductosTransferencia === 'number' ? totalProductosTransferencia : '—') : formatMoney(totalFacturaProcesada)}</p>
                      </div>
                    </div>
                  </div>

                  {paymentRequiredForDeliveryType(tipoEntrega) && (
                  <div className="rounded-[28px] border border-stone-200 bg-white p-5">
                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                      <div>
                        <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-stone-500">Forma de pago del paquete</h3>
                        <p className="mt-1 text-xs text-stone-500">Estos datos se guardan con el paquete al momento de crear y asignar la entrega.</p>
                      </div>
                      {(paquete.pago?.tipo === 'completo' || paquete.pago?.tipo === 'efectivo' || paquete.pago?.tipo === 'cheque' || paquete.pago?.tipo === 'nota_credito') && typeof totalGuiasProcesadas === 'number' && (
                        <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                          Valor total de las facturas: {formatMoney(totalGuiasProcesadas)}
                        </span>
                      )}
                    </div>

                    <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Forma de pago</label>
                        <select
                          value={paquete.pago?.tipo ?? ''}
                          onChange={event => handleTipoPagoChange(event.target.value as FormaPagoPaquete)}
                          className={inputCls}
                        >
                          <option value="">Selecciona una forma de pago</option>
                          <option value="parcial">Por cobrar</option>
                          <option value="completo">Pagado</option>
                          {paquete.pago?.tipo && FORMAS_PAGO_ANTERIORES.includes(paquete.pago.tipo) && (
                            <option value={paquete.pago.tipo}>{formatPaymentLabel(paquete.pago.tipo)} (forma anterior)</option>
                          )}
                        </select>
                      </div>

                      {paquete.pago?.tipo === 'completo' && (
                        <div>
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">¿Cómo fue pagado?</label>
                          <select
                            value={paquete.pago?.medioPago ?? ''}
                            onChange={event => {
                              const medio = event.target.value as TipoPagoParcial;
                              setPaquete(prev => ({
                                ...prev,
                                pago: {
                                  ...(prev.pago ?? createPagoForType('completo')),
                                  tipo: 'completo',
                                  medioPago: medio,
                                  banco: undefined,
                                  numeroTransferencia: undefined,
                                  numeroCheque: undefined,
                                  numeroNotaCredito: undefined,
                                },
                              }));
                            }}
                            className={inputCls}
                          >
                            <option value="">Selecciona cómo se pagó</option>
                            <option value="efectivo">Efectivo</option>
                            <option value="transferencia">Transferencia</option>
                            <option value="cheque">Cheque</option>
                            <option value="nota_credito">Nota de crédito</option>
                          </select>
                        </div>
                      )}

                      {(paquete.pago?.tipo === 'efectivo' || paquete.pago?.tipo === 'completo') && (
                        <div>
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Monto</label>
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={paquete.pago?.monto ?? ''}
                            onWheel={preventNumberInputWheelChange}
                            onChange={event => updatePagoField('monto', parseOptionalNumber(event.target.value))}
                            className={inputCls}
                            placeholder="0.00"
                          />
                        </div>
                      )}

                          {paquete.pago?.tipo === 'completo' && paquete.pago?.medioPago === 'transferencia' && (
                            <>
                              <div>
                                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Número de transferencia</label>
                                <input
                                  value={paquete.pago?.numeroTransferencia ?? ''}
                                  onChange={event => updatePagoField('numeroTransferencia', event.target.value)}
                                  className={inputCls}
                                  placeholder="Referencia bancaria"
                                />
                              </div>
                              <div>
                                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Banco</label>
                                <input
                                  value={paquete.pago?.banco ?? ''}
                                  onChange={event => updatePagoField('banco', event.target.value)}
                                  className={inputCls}
                                  placeholder="Banco emisor"
                                />
                              </div>
                            </>
                          )}
                          {paquete.pago?.tipo === 'completo' && paquete.pago?.medioPago === 'cheque' && (
                            <>
                              <div>
                                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Número de cheque</label>
                                <input
                                  value={paquete.pago?.numeroCheque ?? ''}
                                  onChange={event => updatePagoField('numeroCheque', event.target.value)}
                                  className={inputCls}
                                  placeholder="Número de cheque"
                                />
                              </div>
                              <div>
                                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Banco</label>
                                <input
                                  value={paquete.pago?.banco ?? ''}
                                  onChange={event => updatePagoField('banco', event.target.value)}
                                  className={inputCls}
                                  placeholder="Banco emisor"
                                />
                              </div>
                            </>
                          )}
                          {paquete.pago?.tipo === 'completo' && paquete.pago?.medioPago === 'nota_credito' && (
                            <div>
                              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Número nota de crédito</label>
                              <input
                                value={paquete.pago?.numeroNotaCredito ?? ''}
                                onChange={event => updatePagoField('numeroNotaCredito', event.target.value)}
                                className={inputCls}
                                placeholder="Número de la nota"
                              />
                            </div>
                          )}

                      {paquete.pago?.tipo === 'transferencia' && (
                        <>
                          <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Número de transferencia</label>
                            <input
                              value={paquete.pago?.numeroTransferencia ?? ''}
                              onChange={event => updatePagoField('numeroTransferencia', event.target.value)}
                              className={inputCls}
                              placeholder="Referencia bancaria"
                            />
                          </div>
                          <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Banco</label>
                            <input
                              value={paquete.pago?.banco ?? ''}
                              onChange={event => updatePagoField('banco', event.target.value)}
                              className={inputCls}
                              placeholder="Banco emisor"
                            />
                          </div>
                        </>
                      )}

                      {paquete.pago?.tipo === 'cheque' && (
                        <>
                          <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Número de cheque</label>
                            <input
                              value={paquete.pago?.numeroCheque ?? ''}
                              onChange={event => updatePagoField('numeroCheque', event.target.value)}
                              className={inputCls}
                              placeholder="Número de cheque"
                            />
                          </div>
                          <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Banco</label>
                            <input
                              value={paquete.pago?.banco ?? ''}
                              onChange={event => updatePagoField('banco', event.target.value)}
                              className={inputCls}
                              placeholder="Banco emisor"
                            />
                          </div>
                          <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Monto</label>
                            <input
                              type="number"
                              min={0}
                              step="0.01"
                              value={paquete.pago?.monto ?? ''}
                              onWheel={preventNumberInputWheelChange}
                              onChange={event => updatePagoField('monto', parseOptionalNumber(event.target.value))}
                              className={inputCls}
                              placeholder="0.00"
                            />
                          </div>
                        </>
                      )}

                      {paquete.pago?.tipo === 'nota_credito' && (
                        <>
                          <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Número nota de crédito</label>
                            <input
                              value={paquete.pago?.numeroNotaCredito ?? ''}
                              onChange={event => updatePagoField('numeroNotaCredito', event.target.value)}
                              className={inputCls}
                              placeholder="Número de la nota"
                            />
                          </div>
                          <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Valor</label>
                            <input
                              type="number"
                              min={0}
                              step="0.01"
                              value={paquete.pago?.valorNotaCredito ?? ''}
                              onWheel={preventNumberInputWheelChange}
                              onChange={event => updatePagoField('valorNotaCredito', parseOptionalNumber(event.target.value))}
                              className={inputCls}
                              placeholder="0.00"
                            />
                          </div>
                        </>
                      )}

                      {paquete.pago?.tipo === 'parcial' && (
                        <>
                        <div>
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Forma de cobro sugerida</label>
                          <select
                            value={paquete.pago?.tipoPagoParcial ?? ''}
                            onChange={event => {
                              const nextPartialType = event.target.value as TipoPagoParcial;
                              setPaquete(prev => ({
                                ...prev,
                                pago: {
                                  ...(prev.pago ?? createPagoForType('parcial')),
                                  tipo: 'parcial',
                                  tipoPagoParcial: nextPartialType,
                                  banco: undefined,
                                  numeroTransferencia: undefined,
                                  numeroCheque: undefined,
                                  numeroNotaCredito: undefined,
                                },
                              }));
                            }}
                            className={inputCls}
                          >
                            <option value="">Selecciona cómo se cobrará</option>
                            <option value="efectivo">Efectivo</option>
                            <option value="transferencia">Transferencia</option>
                            <option value="cheque">Cheque</option>
                            <option value="nota_credito">Nota de crédito</option>
                          </select>
                          <p className="mt-1.5 text-xs text-stone-500">Es una sugerencia: el conductor registra la forma de pago real y sus datos al finalizar la entrega.</p>
                        </div>
                        <div>
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Monto por cobrar</label>
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={montoPorCobrarDe(paquete.pago, totalFacturaProcesada) ?? ''}
                            onWheel={preventNumberInputWheelChange}
                            onChange={event => updatePagoField('montoPorCobrar', parseOptionalNumber(event.target.value))}
                            className={inputCls}
                            placeholder="0.00"
                          />
                          <p className="mt-1.5 text-xs text-stone-500">Por defecto, el valor total de la factura.</p>
                        </div>
                        </>
                      )}
                    </div>
                  </div>
                  )}

                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setTipoEntrega('entrega');
                        setTransferenciaDesdeSucursal('');
                        setTransferenciaHaciaSucursal('');
                        setCompaniaTransporte('');
                        setValorTotalEnvio(undefined);
                        setPaquete(createEmptyPaquete());
                        setGuiasProcesadas([]);
                        setUrlDestino('');
                        setPuntosIntermedios([]);
                        setVerificandoPuntoId(null);
                        setTiempoEstimadoMinutos(60);
                        setGuiaError('');
                        setDestinoVerificado(null);
                        setDestinoError('');
                        dismissToast();
                      }}
                      className="rounded-2xl border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 transition hover:bg-stone-100"
                    >
                      Limpiar
                    </button>
                    <button
                      type="submit"
                      disabled={saving || guiaProcesando || envioPdfPendiente}
                      className="rounded-[14px] bg-[var(--freisa-red)] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[var(--freisa-red-dark)] disabled:opacity-60"
                    >
                      {saving ? 'Guardando...' : 'Crear entrega'}
                    </button>
                  </div>
                </form>
              </div>
            </section>
          )}

          {section === 'mapa' && (
            <section className="space-y-5">
              <div>
                <h3 className="text-2xl font-semibold text-stone-950">Mapa por conductor</h3>
                <p className="text-sm text-stone-600">Consulta las entregas que asignaste a ese conductor y elige cual seguir en el mapa segun el estado real del viaje.</p>
              </div>

              {conductoresConEntregas.length === 0 ? (
                <div className="rounded-[30px] border border-dashed border-stone-300 bg-white/70 p-12 text-center text-stone-500">
                  Aun no hay conductores activos disponibles.
                </div>
              ) : !mapaConductorId ? (
                <div className="rounded-[32px] border border-white/80 bg-white/85 p-6 shadow-[0_24px_60px_rgba(120,113,108,0.12)]">
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
                    Selecciona un conductor
                  </label>
                  <select
                    value={mapaConductorId}
                    onChange={event => {
                      setMapaConductorId(event.target.value);
                      setMapaViaje(null);
                      setMapaViajeId(null);
                      setEntregasConductorSeleccionado([]);
                      setMapaError('');
                    }}
                    className={inputCls}
                  >
                    <option value="">Selecciona un conductor</option>
                    {conductoresConEntregas.map(conductor => (
                      <option key={conductor.id} value={conductor.id}>
                        {conductor.nombre}{conductor.placa ? ` · ${conductor.placa}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <>
                  <div className="flex flex-col gap-3 rounded-[32px] border border-white/80 bg-white/85 p-5 shadow-[0_24px_60px_rgba(120,113,108,0.12)] md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-lg font-semibold text-stone-900">{conductorMapa?.nombre || 'Conductor seleccionado'}</p>
                      <p className="text-sm text-stone-500">{conductorMapa?.placa || 'Sin placa asignada'} · Seguimiento individual bajo demanda</p>
                    </div>
                    <button
                      onClick={() => {
                        setMapaConductorId('');
                        setMapaViaje(null);
                        setMapaViajeId(null);
                        setEntregasConductorSeleccionado([]);
                        setMapaError('');
                      }}
                      className="rounded-2xl border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 transition hover:bg-stone-100"
                    >
                      Cambiar conductor
                    </button>
                  </div>

                  {mapaError && (
                    <div className="rounded-[24px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                      {mapaError}
                    </div>
                  )}

                  {mapaCargando && !mapaViaje && (
                    <div className="rounded-[24px] border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                      Cargando ubicacion del conductor...
                    </div>
                  )}

                  <div className="grid gap-4 xl:grid-cols-[1.35fr_0.65fr]">
                    <div className="space-y-4">
                      {!mapaCargando && !mapaViaje && !mapaError && (
                        <div className="rounded-[30px] border border-dashed border-stone-300 bg-white/70 p-12 text-center text-stone-500">
                          Este conductor no tiene una entrega en camino en este momento.
                        </div>
                      )}

                      {mapaViaje && (
                        <div className="rounded-[32px] border border-white/80 bg-white/85 p-5 shadow-[0_24px_60px_rgba(120,113,108,0.12)]">
                          <div className="mb-4 flex items-center justify-between gap-3">
                            <div>
                              <p className="text-lg font-semibold text-stone-900">{conductorMapa?.nombre || 'Conductor'}</p>
                              <p className="text-sm text-stone-500">{conductorMapa?.placa || 'Sin placa'} · {mapaViaje.paquete?.nombre || 'Sin paquete'}</p>
                            </div>
                            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${estadoBadge(mapaViaje)}`}>{estadoLabel(mapaViaje)}</span>
                          </div>
                          {mapaViaje.coordenadas.length > 0 || mapaViaje.coordenadaActual || mapaViaje.coordenadaFin || mapaViaje.puntosIntermedios.length > 0 ? (
                            <MapaAdmin
                              key={mapaViaje.id}
                              coordenadas={mapaViaje.coordenadas}
                              coordenadaActual={mapaViaje.coordenadaActual}
                              coordenadaInicio={mapaViaje.coordenadaInicio}
                              coordenadaFin={mapaViaje.coordenadaFin}
                              puntosIntermedios={mapaViaje.puntosIntermedios}
                              conductorNombre={conductorMapa?.nombre}
                            />
                          ) : (
                            <div className="rounded-[24px] border border-dashed border-stone-300 bg-stone-50 px-4 py-10 text-center text-sm text-stone-500">
                              Este viaje todavia no transmite ubicacion en tiempo real. Cuando el conductor lo inicie, el mapa se actualizara aqui.
                            </div>
                          )}
                          <div className="mt-4 grid gap-2 text-sm text-stone-600">
                            {mapaViaje.tipoEntrega !== 'transferencia' && (
                              <>
                                <p>Cliente: {mapaViaje.paquete?.cliente?.nombre || '—'}</p>
                                <p>Correo: {mapaViaje.paquete?.cliente?.correoElectronico || '—'}</p>
                              </>
                            )}
                            <p>Valor total factura: {formatMoney(mapaViaje.paquete?.valorTotalFactura)}</p>
                            <p>Puntos intermedios: {mapaViaje.puntosIntermedios.length}</p>
                            <p>Tiempo estimado: {mapaViaje.tiempoEstimadoMinutos ? `${mapaViaje.tiempoEstimadoMinutos} min` : '—'}</p>
                            <p>Ultima actualizacion: {formatDateTime(mapaViaje.fechaFin ?? mapaViaje.fechaInicio)}</p>
                            {mapaViaje.urlDestino && (
                              <p>
                                <a href={mapaViaje.urlDestino} target="_blank" rel="noreferrer" className="font-medium text-teal-700 underline-offset-4 hover:underline">
                                  Abrir destino configurado
                                </a>
                              </p>
                            )}
                            {mapaViaje.puntosIntermedios.length > 0 && (
                              <div className="flex flex-wrap gap-2 pt-1">
                                {mapaViaje.puntosIntermedios.map((point, index) => (
                                  <a
                                    key={`${mapaViaje.id}-map-point-${index}`}
                                    href={point.urlDestino}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100"
                                  >
                                    Punto {index + 1}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="rounded-[32px] border border-white/80 bg-white/85 p-5 shadow-[0_24px_60px_rgba(120,113,108,0.12)]">
                      <h4 className="text-lg font-semibold text-stone-900">Entregas que le asignaste</h4>
                      <p className="mt-1 text-sm text-stone-500">Este listado consulta al backend solo por el conductor seleccionado para no cargar todas tus entregas.</p>
                      <div className="mt-4 space-y-3">
                        {entregasConductorSeleccionado.length === 0 ? (
                          <div className="rounded-[24px] border border-dashed border-stone-300 bg-stone-50 p-6 text-center text-sm text-stone-500">
                            No tienes entregas para este conductor.
                          </div>
                        ) : (
                          entregasConductorSeleccionado.map(viaje => (
                            <article key={viaje.id} className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-4 text-sm text-stone-600">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="font-semibold text-stone-900">{viaje.paquete?.nombre || 'Paquete sin nombre'}</p>
                                  {viaje.tipoEntrega !== 'transferencia' && (
                                    <p className="mt-1">{viaje.paquete?.cliente?.nombre || 'Sin cliente registrado'}</p>
                                  )}
                                </div>
                                <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${estadoBadge(viaje)}`}>
                                  {estadoLabel(viaje)}
                                </span>
                              </div>
                              <p className="mt-3">Actualizado: {formatDateTime(viaje.fechaFin ?? viaje.fechaInicio)}</p>
                              <p className="mt-1">Valor total factura: {formatMoney(viaje.paquete?.valorTotalFactura)}</p>
                              {viaje.estado === 'en_curso' ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setMapaViaje(viaje);
                                    setMapaViajeId(viaje.id);
                                    setMapaError('');
                                  }}
                                  className="mt-3 rounded-full bg-blue-50 px-3 py-1.5 font-medium text-blue-700 transition hover:bg-blue-100"
                                >
                                  Seguir este viaje en el mapa
                                </button>
                              ) : viaje.estado === 'finalizado' && hasMapData(viaje) ? (
                                <button
                                  type="button"
                                  onClick={() => setHistorialMapaViaje(viaje)}
                                  className="mt-3 rounded-full bg-emerald-50 px-3 py-1.5 font-medium text-emerald-700 transition hover:bg-emerald-100"
                                >
                                  Ver recorrido final
                                </button>
                              ) : null}
                            </article>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </section>
          )}

          {historialMapaViaje && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-950/55 px-4 py-6">
              <div className="max-h-full w-full max-w-5xl overflow-y-auto rounded-[32px] border border-white/70 bg-white shadow-[0_40px_120px_rgba(28,25,23,0.35)]">
                <div className="sticky top-0 flex items-center justify-between border-b border-stone-200 bg-white px-6 py-4">
                  <div>
                    <h3 className="text-lg font-semibold text-stone-950">Recorrido de la entrega</h3>
                    <p className="text-sm text-stone-500">
                      {conductores.find(conductor => conductor.id === historialMapaViaje.conductorId)?.nombre || 'Conductor'}
                      {' · '}
                      {historialMapaViaje.paquete?.nombre || 'Sin paquete'}
                    </p>
                  </div>
                  <button
                    onClick={() => setHistorialMapaViaje(null)}
                    className="rounded-2xl border border-stone-200 px-3 py-2 text-sm font-medium text-stone-600 transition hover:bg-stone-100"
                  >
                    Cerrar
                  </button>
                </div>

                <div className="space-y-4 px-6 py-5">
                  <MapaAdmin
                    key={`historial-${historialMapaViaje.id}`}
                    coordenadas={historialMapaViaje.coordenadas}
                    coordenadaActual={historialMapaViaje.coordenadaActual}
                    coordenadaInicio={historialMapaViaje.coordenadaInicio}
                    coordenadaFin={historialMapaViaje.coordenadaFin}
                    puntosIntermedios={historialMapaViaje.puntosIntermedios}
                    conductorNombre={conductores.find(conductor => conductor.id === historialMapaViaje.conductorId)?.nombre}
                  />
                  <div className="grid gap-2 text-sm text-stone-600 md:grid-cols-2">
                    <p>Valor total factura: {formatMoney(historialMapaViaje.paquete?.valorTotalFactura)}</p>
                    <p>Coordenadas registradas: {historialMapaViaje.coordenadas.length}</p>
                    <p>Puntos intermedios: {historialMapaViaje.puntosIntermedios.length}</p>
                    <p>Inicio: {formatDateTime(historialMapaViaje.fechaInicio)}</p>
                    <p>Fin: {formatDateTime(historialMapaViaje.fechaFin)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {detalleEntrega && (() => {
            const conductor = conductores.find(candidate => candidate.id === detalleEntrega.conductorId);
            const fotosEntrega = detalleEntrega.imagenesEntrega?.length
              ? detalleEntrega.imagenesEntrega
              : detalleEntrega.imagenEntrega
                ? [detalleEntrega.imagenEntrega]
                : [];
            const invoiceNumbers = getTripInvoiceNumbers(detalleEntrega);
            return (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-950/55 px-4 py-6">
                <div className="max-h-full w-full max-w-4xl overflow-y-auto rounded-[32px] border border-white/70 bg-white shadow-[0_40px_120px_rgba(28,25,23,0.35)]">
                  <div className="sticky top-0 flex items-center justify-between border-b border-stone-200 bg-white px-6 py-4">
                    <div>
                      <h3 className="text-lg font-semibold text-stone-950">Detalle de la entrega</h3>
                      <p className="text-sm text-stone-500">{detalleEntrega.paquete?.nombre || 'Entrega sin nombre'} · {conductor?.nombre || 'Pendiente de asignación'}</p>
                    </div>
                    <button
                      onClick={() => setDetalleEntrega(null)}
                      className="rounded-2xl border border-stone-200 px-3 py-2 text-sm font-medium text-stone-600 transition hover:bg-stone-100"
                    >
                      Cerrar
                    </button>
                  </div>

                  <div className="space-y-4 px-6 py-5">
                    <div className="grid gap-4 md:grid-cols-2">
                      {detalleEntrega.tipoEntrega !== 'transferencia' && (
                        <>
                          <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Cliente</p>
                            <p className="mt-2 text-base font-semibold text-stone-900">{detalleEntrega.paquete?.cliente?.nombre || '—'}</p>
                          </div>
                          <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Correo cliente</p>
                            <p className="mt-2 text-base font-semibold text-stone-900">{detalleEntrega.paquete?.cliente?.correoElectronico || '—'}</p>
                          </div>
                        </>
                      )}
                      <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Sucursal</p>
                        <p className="mt-2 text-base font-semibold text-stone-900">{detalleEntrega.sucursal?.nombre || detalleEntrega.vendedor?.sucursal?.nombre || '—'}</p>
                      </div>
                      <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Descripción</p>
                        <p className="mt-2 text-sm text-stone-700">{detalleEntrega.paquete?.descripcion || '—'}</p>
                      </div>
                      <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Valor total factura</p>
                        <p className="mt-2 text-base font-semibold text-stone-900">{formatMoney(detalleEntrega.paquete?.valorTotalFactura)}</p>
                      </div>
                      <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4 md:col-span-2">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
                          {invoiceNumbers.length > 1 ? 'Números de factura' : 'Número de factura'}
                        </p>
                        {invoiceNumbers.length > 0 ? (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {invoiceNumbers.map(numeroFactura => (
                              <span
                                key={`${detalleEntrega.id}-factura-${numeroFactura}`}
                                className="rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-stone-700 shadow-sm ring-1 ring-stone-200"
                              >
                                {numeroFactura}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-2 text-base font-semibold text-stone-900">—</p>
                        )}
                      </div>
                      <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Forma de pago</p>
                        <p className="mt-2 text-base font-semibold text-stone-900">{formatPaymentLabel(detalleEntrega.paquete?.pago?.tipo)}</p>
                      </div>
                      <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Tipo de entrega</p>
                        <p className="mt-2 text-base font-semibold text-stone-900">{formatDeliveryTypeLabel(detalleEntrega.tipoEntrega)}</p>
                      </div>
                      <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Estado</p>
                        <p className="mt-2 text-base font-semibold text-stone-900">{historialEstadoLabel(detalleEntrega)}</p>
                      </div>
                      <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Devolución</p>
                        <p className="mt-2 text-base font-semibold text-stone-900">{detalleEntrega.esDevolucion ? 'Sí' : 'No'}</p>
                      </div>
                      {detalleEntrega.tipoEntrega === 'envio' && (
                        <>
                          <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Valor total de envío</p>
                            <p className="mt-2 text-base font-semibold text-stone-900">{formatMoney(detalleEntrega.valorTotalEnvio)}</p>
                          </div>
                          <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Compañía de transporte</p>
                            <p className="mt-2 text-base font-semibold text-stone-900">{detalleEntrega.companiaTransporte || '—'}</p>
                          </div>
                        </>
                      )}
                      <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Puntos intermedios</p>
                        <p className="mt-2 text-base font-semibold text-stone-900">{detalleEntrega.puntosIntermedios.length}</p>
                      </div>
                      <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Tiempo estimado</p>
                        <p className="mt-2 text-base font-semibold text-stone-900">{detalleEntrega.tiempoEstimadoMinutos ? `${detalleEntrega.tiempoEstimadoMinutos} min` : '—'}</p>
                      </div>
                      <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Inicio</p>
                        <p className="mt-2 text-base font-semibold text-stone-900">{formatDateTime(detalleEntrega.fechaInicio)}</p>
                      </div>
                      <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Fin</p>
                        <p className="mt-2 text-base font-semibold text-stone-900">{formatDateTime(detalleEntrega.fechaFin)}</p>
                      </div>
                      {detalleEntrega.observaciones && (
                        <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4 md:col-span-2">
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Observaciones</p>
                          <p className="mt-2 text-sm text-stone-700">{detalleEntrega.observaciones}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-3 text-sm">
                      {(() => {
                        const documentos = getTripDocumentos(detalleEntrega);
                        if (documentos.documentos.length === 0) return null;

                        return documentos.documentos.map((documento, index) => (
                          <a
                            key={`${detalleEntrega.id}-documento-${index}`}
                            href={documento.url}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-full bg-emerald-50 px-3 py-1.5 font-medium text-emerald-700 transition hover:bg-emerald-100"
                          >
                            {documento.nombre}
                          </a>
                        ));
                      })()}
                      {detalleEntrega.urlDestino && (
                        <a
                          href={detalleEntrega.urlDestino}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-full bg-stone-100 px-3 py-1.5 font-medium text-stone-700 transition hover:bg-stone-200"
                        >
                          Abrir destino
                        </a>
                      )}
                      {detalleEntrega.puntosIntermedios.map((point, index) => (
                        <a
                          key={`${detalleEntrega.id}-detail-point-${index}`}
                          href={point.urlDestino}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-full bg-emerald-50 px-3 py-1.5 font-medium text-emerald-700 transition hover:bg-emerald-100"
                        >
                          Punto {index + 1}
                        </a>
                      ))}
                      {detalleEntrega.pdfGuiaFirmadoUrl && (
                        <a
                          href={detalleEntrega.pdfGuiaFirmadoUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-full bg-emerald-50 px-3 py-1.5 font-medium text-emerald-700 transition hover:bg-emerald-100"
                        >
                          Ver guia firmada
                        </a>
                      )}
                      {detalleEntrega.estado === 'en_curso' && conductor && (
                        <button
                          type="button"
                          onClick={() => {
                            setDetalleEntrega(null);
                            setSection('mapa');
                            setMapaConductorId(conductor.id);
                            setMapaViaje(detalleEntrega);
                            setMapaViajeId(detalleEntrega.id);
                            setMapaError('');
                          }}
                          className="rounded-full bg-red-50 px-3 py-1.5 font-medium text-red-700 transition hover:bg-red-100"
                        >
                          Ver mapa del conductor
                        </button>
                      )}
                      {detalleEntrega.estado === 'finalizado' && hasMapData(detalleEntrega) && (
                        <button
                          type="button"
                          onClick={() => {
                            setDetalleEntrega(null);
                            setHistorialMapaViaje(detalleEntrega);
                          }}
                          className="rounded-full bg-emerald-50 px-3 py-1.5 font-medium text-emerald-700 transition hover:bg-emerald-100"
                        >
                          Ver recorrido final
                        </button>
                      )}
                    </div>

                    {detalleEntrega.estado === 'finalizado' ? (
                      <div className="grid gap-6 lg:grid-cols-2">
                        <div className="rounded-[24px] border border-stone-200 p-4">
                          <h4 className="mb-3 font-semibold text-stone-900">Fotos del paquete</h4>
                          {(detalleEntrega.paquete?.imagenes?.length ?? 0) > 0 ? (
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                              {detalleEntrega.paquete?.imagenes.map((image, index) => (
                                <a
                                  key={`${detalleEntrega.id}-image-${index}`}
                                  href={image}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="block overflow-hidden rounded-[20px] border border-stone-200 bg-stone-50"
                                >
                                  <img src={image} alt={`Paquete ${index + 1}`} className="h-40 w-full object-cover" loading="lazy" />
                                </a>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-stone-500">No hay imágenes del paquete.</p>
                          )}
                        </div>

                        <div className="rounded-[24px] border border-stone-200 p-4">
                          <h4 className="mb-3 font-semibold text-stone-900">Fotos de entrega</h4>
                          {fotosEntrega.length > 0 ? (
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                              {fotosEntrega.map((image, index) => (
                                <a
                                  key={`${detalleEntrega.id}-entrega-${index}`}
                                  href={image}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="block overflow-hidden rounded-[20px] border border-emerald-200 bg-emerald-50"
                                >
                                  <img src={image} alt={`Entrega final ${index + 1}`} className="h-40 w-full object-cover" loading="lazy" />
                                </a>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-stone-500">No hay imágenes de entrega.</p>
                          )}
                        </div>
                      </div>
                    ) : (
                      (detalleEntrega.paquete?.imagenes?.length ?? 0) > 0 && (
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {detalleEntrega.paquete?.imagenes.map((image, index) => (
                            <a
                              key={`${detalleEntrega.id}-image-${index}`}
                              href={image}
                              target="_blank"
                              rel="noreferrer"
                              className="block overflow-hidden rounded-[20px] border border-stone-200 bg-stone-50"
                            >
                              <img src={image} alt={`Entrega ${index + 1}`} className="h-40 w-full object-cover" loading="lazy" />
                            </a>
                          ))}
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

      {envioPdfDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-950/55 px-4 py-6">
          <div className="max-h-full w-full max-w-3xl overflow-y-auto rounded-[32px] border border-white/70 bg-white shadow-[0_40px_120px_rgba(28,25,23,0.35)]">
            <div className="sticky top-0 flex items-center justify-between border-b border-stone-200 bg-white px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-stone-950">Generar PDF de envío</h3>
                <p className="text-sm text-stone-500">Revisa y ajusta los datos antes de guardar y descargar el PDF horizontal.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (generandoEnvioPdf) return;
                  setEnvioPdfDraft(null);
                  setEnvioPdfError('');
                }}
                className="rounded-2xl border border-stone-200 px-3 py-2 text-sm font-medium text-stone-600 transition hover:bg-stone-100"
              >
                Cerrar
              </button>
            </div>

            <div className="space-y-5 px-6 py-5">
              {envioPdfError && (
                <div className="rounded-[20px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {envioPdfError}
                </div>
              )}

              <div className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Logo de factura</p>
                {envioPdfDraft.logoUrl ? (
                  <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-center">
                    <img src={envioPdfDraft.logoUrl} alt="Logo factura" className="h-16 w-auto rounded-2xl border border-stone-200 bg-white p-2" loading="lazy" />
                    <p className="text-sm text-stone-600 break-all">{envioPdfDraft.logoUrl}</p>
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-stone-600">No se encontró un valor público en logo_factura_url. El PDF se puede descargar sin logo.</p>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Destino/Transporte</label>
                  <textarea
                    rows={3}
                    value={envioPdfDraft.entrega}
                    onChange={event => setEnvioPdfDraft(prev => (prev ? { ...prev, entrega: event.target.value } : prev))}
                    required
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Dirección</label>
                  <textarea
                    rows={3}
                    value={envioPdfDraft.direccion}
                    onChange={event => setEnvioPdfDraft(prev => (prev ? { ...prev, direccion: event.target.value } : prev))}
                    required
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Cliente</label>
                  <input
                    value={envioPdfDraft.cliente}
                    onChange={event => setEnvioPdfDraft(prev => (prev ? { ...prev, cliente: event.target.value } : prev))}
                    required
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">CI</label>
                  <input
                    value={envioPdfDraft.identificacion}
                    onChange={event => setEnvioPdfDraft(prev => (prev ? { ...prev, identificacion: event.target.value } : prev))}
                    required
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">#TELF</label>
                  <input
                    value={envioPdfDraft.telefono}
                    onChange={event => setEnvioPdfDraft(prev => (prev ? { ...prev, telefono: event.target.value } : prev))}
                    required
                    className={inputCls}
                  />
                </div>
              </div>

              {envioPdfDraftValidationError && !envioPdfError && (
                <div className="rounded-[18px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  {envioPdfDraftValidationError}
                </div>
              )}

              <div className="flex flex-wrap justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (generandoEnvioPdf) return;
                    setEnvioPdfDraft(null);
                    setEnvioPdfError('');
                  }}
                  className="rounded-[14px] border border-stone-300 px-4 py-2.5 text-sm font-medium text-stone-700 transition hover:bg-stone-100"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => void handleConfirmarEnvioPdf()}
                  disabled={generandoEnvioPdf || Boolean(envioPdfDraftValidationError)}
                  className="rounded-[14px] bg-[var(--freisa-red)] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[var(--freisa-red-dark)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {generandoEnvioPdf ? 'Generando y guardando PDF...' : 'Confirmar, guardar y descargar PDF'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PanelShell>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}